import React, { useState, useEffect, useContext, useRef } from "react";
import { Container, Row, Col, Tab, Nav, Form, Button, Table, OverlayTrigger, Tooltip, Modal } from "react-bootstrap";
import ReactSlider from "react-slider";
import { useLocation } from "react-router-dom"; // <-- for reading URL params
import Sidebar from "./Sidebar";
import axios from "axios";
import { useProfile } from "../contexts/ProfileContext";
import Balance, { BalanceTopUp, closestTier } from "./Balance";
import { API_BASE_URL } from '../config/api';
import axiosClient from "../api/axiosClient";
import Alert from "./Alert";
import { useLanguage } from "../contexts/LanguageContext";
import { QUALITY_TIERS, DEFAULT_QUALITY, qualityPrice } from "../config/packages";


// Brand colouring lives in styles/boost.css: Font Awesome draws its glyph in a
// ::before, so the gradient has to be clipped from a real stylesheet — inline it
// painted the element box and showed as a solid square.
const brandClass = (platform) => `brand-icon brand-${platform || 'unknown'}`;

// Fixed dashboard follower packages. The server owns these same anchor prices;
// this list only controls which clean, one-click choices are shown.
const FOLLOWER_TIERS = [500, 1200, 2500, 6500];

// Larger packages stay behind Custom amount so the default ladder is quick to
// scan. Prices still come from the server-supplied curve at render time; only
// the quantities live here.
const CUSTOM_FOLLOWER_TIERS = [14000, 25000, 50000];

// Two round amounts per service, priced from the live catalog rate. Two rather
// than five: the bundles below are the real offer here, and a long tier list
// competes with them.
// Views are 6x cheaper per 1,000 than likes, so they get larger amounts —
// otherwise the two view tiers land at trivial prices next to the likes ones.
const SERVICE_TIERS = { likes: [1000, 5000], views: [10000, 50000] };

// Engagement bundles spread across recent posts, which needs the post lookup —
// only Instagram and TikTok have one.
const BUNDLE_PLATFORMS = ['instagram', 'tiktok'];

// Mirrors ENGAGEMENT_PACKS in routes/boosts.js — the server prices these and is
// the authority on what gets charged.
const ENGAGEMENT_PACKS = [
	{ id: 'starter', likes: 5000, views: 10000, price: 10, icon: '❤️' },
	{ id: 'growth', likes: 15000, views: 30000, price: 25, icon: '😍' },
	{ id: 'max', likes: 30000, views: 75000, price: 50, icon: '❤️‍🔥' },
];

// Paid targeting add-ons, offered once a boost is on its way. Same three the
// checkout sells, priced identically (routes/purchase.js CUSTOMIZE_ADDONS).
// Icons match the ones the same three add-ons carry on the visualizer's
// customize step, so a customer who saw them at checkout recognises them here.
const BOOST_ADDONS = [
	{ key: 'fast', icon: '🚀' },
	{ key: 'female', icon: '👩' },
	{ key: 'usa', icon: '🇺🇸' },
];

// An extra costs a share of the cart rather than a flat fee: $4.99 was 83% of
// a $6 order and 3% of a $150 one. Mirrored by ADDON_SHARE / ADDON_MIN in
// routes/purchase.js, which is what actually charges.
const ADDON_SHARE = 0.25;
const ADDON_MIN = 1.99;
const addonPriceFor = (cart) => Math.max(ADDON_MIN, Math.round((Number(cart) || 0) * ADDON_SHARE * 100) / 100);

const DeliveryDashboard = () => {
	const L = useLanguage();
	const profile = useProfile();
	const [followersValue, setFollowersValue] = useState(0);
	const [likesValue, setLikesValue] = useState(0);
	const [viewsValue, setViewsValue] = useState(0);
	const [commentsValue, setCommentsValue] = useState(0);
	const [postLink, setPostLink] = useState("");
	const [orders, setOrders] = useState([]);
	const [ordersLiveLoaded, setOrdersLiveLoaded] = useState(false);
	// True once the full (live-progress) response has landed, so the fast
	// response can never overwrite it if the two resolve out of order.
	const liveOrdersRef = useRef(false);
	const ordersRef = useRef(null);
	const [orderTable, updateOrderTable] = useState([]);
	// Which paid-but-undelivered order is currently being sent.
	const [sendingPackId, setSendingPackId] = useState(null);
	const [price, setPrice] = useState(0);
	const [normalPrice, setNormalPrice] = useState(0);
	const [quantity, setQuantity] = useState(0);
	const [balanceModal, setBalanceModal] = useState(false);
	const [platform, setPlatform] = useState("instagram");
	const [comments, setComments] = useState("");
	const [min, setMin] = useState(0);
	const [posts, setPosts] = useState([]);
	const [cursors, setCursors] = useState({ 0: 0 });
	const [currentPage, setCurrentPage] = useState(0);
	const [pendingSend, setPendingSend] = useState(false);

	const [selectedPost, setSelectedPost] = useState(null);
	// Posts an order is spread across. selectedPost stays the single-post value
	// the rest of the flow already uses; this is the multi-post superset.
	const [selectedPosts, setSelectedPosts] = useState([]);
	// The handle is collected here now rather than on the page, so an order
	// cannot reach checkout without a destination — a followers order needs no
	// post, so nothing else was stopping it being bought with an empty link.
	const [showProfileModal, setShowProfileModal] = useState(false);

	// Preview of the account the typed handle points at, so the buyer confirms
	// a face rather than trusting their spelling — a typo here ships real
	// followers to a stranger. Fail-open: a lookup that errors shows a warning
	// but never blocks the purchase.
	const [profilePreview, setProfilePreview] = useState(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewFailed, setPreviewFailed] = useState(false);
	const previewTimer = useRef(null);
	const previewSeq = useRef(0);
	// Set when a purchase is stopped by an empty balance, so the top-up can hand
	// straight back to it instead of dropping the customer where they started.
	const [resumeAfterTopup, setResumeAfterTopup] = useState(false);
	// The pay step shown when balance won't cover a boost: buy this one outright,
	// or add funds and take the bonus. null = closed, 'choose' = the two options,
	// 'exact' = paying this boost's price, 'topup' = the ladder.
	const [payStep, setPayStep] = useState(null);
	const [payAmount, setPayAmount] = useState(0);
	const [detailsOrder, setDetailsOrder] = useState(null);
	// Quick-select tiers for followers; custom opens the input + slider.
	const [customFollowers, setCustomFollowers] = useState(false);
	const [customLikes, setCustomLikes] = useState(false);
	const [customViews, setCustomViews] = useState(false);
	const [packLoading, setPackLoading] = useState(false);
	// A bundle is selected, not bought on click — it flows into the order summary
	// so the customer confirms the charge like any other boost.
	const [selectedPack, setSelectedPack] = useState(null);
	// Offered after a boost is placed, not before — the order is already on its
	// way, so this adds to it rather than standing between them and buying.
	const [showAddons, setShowAddons] = useState(false);
	const [chosenAddons, setChosenAddons] = useState([]);
	const [addonLoading, setAddonLoading] = useState(false);

	// The configuration they last bought, offered back rather than asked for
	// again. Seeded from the profile — the checkout writes quality, genderTarget,
	// countries and upsells on every package purchase — and editable in place.
	const [quality, setQuality] = useState(DEFAULT_QUALITY);
	const [optUsa, setOptUsa] = useState(false);
	const [optFemale, setOptFemale] = useState(false);
	const [optFast, setOptFast] = useState(false);
	const [optsSeeded, setOptsSeeded] = useState(false);

	const [functionError, setFunctionError] = useState("");
	const [functionSuccess, setFunctionSuccess] = useState("");


	// Read "mode" from query param (?mode=likes/views/comments)
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const modeParam = queryParams.get("mode");

	// We'll ensure it's one of the 3 valid tabs; otherwise default to "likes"
	const validModes = ["likes", "views", "comments", "followers"];
	const defaultKey = validModes.includes(modeParam) ? modeParam : "followers";

	const [service, setService] = useState(defaultKey)
	// Handler for "Start Delivery"
	const handleStartDelivery = async (type) => {
		try {
			if (pendingSend) {
				setFunctionError(L.booster.waitPrevious);
				return;
			}
			let selectedValue;
			if (type === "likes") selectedValue = likesValue;
			if (type === "views") selectedValue = viewsValue;
			if (type === "comments") selectedValue = commentsValue;
			if (type === "followers") selectedValue = followersValue;


			var finalLink = postLink;
			if (selectedPost) {
				finalLink = selectedPost;
			}


			const data = {
				"serviceId": platform + "-" + type,
				"link": finalLink,
				"quantity": selectedValue,
				"comments": comments,
				// The grade chosen in the options panel. The server multiplies its
				// own price by it — this key never carries an amount.
				...(type === 'followers' ? { quality } : {}),
				...(type === 'followers' ? { addons: chosenAddons } : {}),
				// The server re-derives the per-post split and re-checks it against
				// the panel minimum; this is the selection, not the arithmetic.
				...(selectedPosts.length > 1 ? { links: selectedPosts } : {}),
			}

			setPendingSend(true);
			await axiosClient.post("https://api.viralgpt.com/boosts/order", data);

			updateOrderTable("!");
			profile.balance = (Number(profile.balance) || 0) - (Number(price) || 0);
			setFunctionSuccess(L.booster.startingDelivery(type, selectedValue, postLink, price));
			setPendingSend(false);
			setTimeout(() => window.location.reload(), 1200);
		} catch (e) {
			setFunctionError(L.booster.failedCreateOrder(e.response.data.error));
			if (e.response.data.error === "Insufficient balance") {
				setResumeAfterTopup(true);
				setBalanceModal(true);
			}
		} finally {
			setPendingSend(false);
		}
	};


	// What each chosen post receives, and whether the split is legal. The panel
	// rejects anything under its own minimum, so a split that dips below it
	// would quietly deliver less than was paid for.
	const splitFor = (kind, total, postCount) => {
		const min = prices[platform + '-' + kind]?.min || 1;
		const per = postCount > 0 ? Math.floor(Number(total || 0) / postCount) : 0;
		return { per, min, ok: per >= min };
	};

	// The most posts this order can cover without any of them dropping below the
	// minimum — used to stop selection rather than fail at submit.
	const maxPostsFor = (kind, total) => {
		const min = prices[platform + '-' + kind]?.min || 1;
		return Math.max(1, Math.floor(Number(total || 0) / min));
	};

	// A bundle spreads likes and views together, so the tighter of the two caps.
	const activeSplit = () => {
		if (selectedPack) {
			const n = selectedPosts.length || 1;
			return [
				{ kind: 'likes', ...splitFor('likes', selectedPack.likes, n) },
				{ kind: 'views', ...splitFor('views', selectedPack.views, n) },
			];
		}
		const total = service === 'likes' ? likesValue : service === 'views' ? viewsValue : commentsValue;
		return [{ kind: service, ...splitFor(service, total, selectedPosts.length || 1) }];
	};

	const maxSelectablePosts = () => {
		if (selectedPack) return Math.min(maxPostsFor('likes', selectedPack.likes), maxPostsFor('views', selectedPack.views));
		const total = service === 'likes' ? likesValue : service === 'views' ? viewsValue : commentsValue;
		return maxPostsFor(service, total);
	};

	const togglePost = (url) => {
		setSelectedPosts((prev) => {
			if (prev.includes(url)) {
				const next = prev.filter((u) => u !== url);
				setSelectedPost(next[0] || null);
				return next;
			}
			if (prev.length >= maxSelectablePosts()) return prev;
			const next = [...prev, url];
			setSelectedPost(next[0]);
			return next;
		});
	};

	// The avatar belongs to whichever linked account this platform resolves to,
	// and only while the field still holds that handle — showing someone's photo
	// beside a different account would misrepresent the order.
	const linkedProfile = () => linkedForPlatform();
	const deliveringToLinkedProfile = () => {
		const match = linkedForPlatform();
		return !!match && String(postLink || '').replace(/^@/, '').trim().toLowerCase() === String(match.username).toLowerCase();
	};

	// The linked profile for the selected platform, preferring the active one.
	// A customer with both an Instagram and a TikTok account linked should get
	// the right handle on each tab, not just whichever is currently selected.
	const linkedForPlatform = () => {
		const socials = profile?.socials || {};
		const active = socials[profile?.selectedProfile];
		if (active?.platform === platform && active.username) return active;
		return Object.values(socials).find((x) => x?.platform === platform && x?.username) || null;
	};

	// Prefill when a linked account exists for this platform; otherwise ask.
	// Handles are not portable between networks, so a mismatch clears rather
	// than carrying a name that would boost a stranger's account elsewhere.
	useEffect(() => {
		const match = linkedForPlatform();
		if (match) {
			setPostLink((cur) => (cur ? cur : match.username));
			return;
		}
		// Cleared, not carried: the CTA reads this as "no destination yet" and
		// opens the profile popup, so a platform with no linked account asks for
		// a handle at the point of buying rather than on the page.
		setPostLink('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [platform, profile?.selectedProfile]);

	const buyEngagementPack = async (pack) => {
		if (packLoading) return;
		setPackLoading(true);
		setPendingSend(true);
		setFunctionError("");
		try {
			await axiosClient.post("https://api.viralgpt.com/boosts/engagement-pack", {
				packId: pack.id,
				platform,
				// Omitted means "spread over recent posts", which is what a bundle
				// does by default.
				...(selectedPosts.length > 0 ? { links: selectedPosts } : {}),
			});
			setFunctionSuccess(L.booster.bundleQueued);
			setTimeout(() => window.location.reload(), 1500);
		} catch (e) {
			const msg = e.response?.data?.error || "";
			setFunctionError(msg || L.booster.bundleFailed);
			if (msg === "Insufficient balance") { setResumeAfterTopup(true); setBalanceModal(true); }
		} finally {
			setPackLoading(false);
			setPendingSend(false);
		}
	};

	// The single point where money moves. Add-ons are applied first: they change
	// how the boost is delivered, so buying them after would leave the order
	// already dispatched under the old targeting.
	const confirmPurchase = async () => {
		setAddonLoading(true);
		setFunctionError("");
		try {
			if (chosenAddons.length > 0) {
				await axiosClient.post("https://api.viralgpt.com/purchase/addons", {
					addons: chosenAddons,
					// The server re-derives the cart from these; it does not take a
					// price from the client.
					...(selectedPack ? { packId: selectedPack.id } : { service: platform + '-' + service, quantity }),
				});
			}
			setShowAddons(false);
			if (selectedPack) await buyEngagementPack(selectedPack);
			else await handleStartDelivery(service);
		} catch (e) {
			setFunctionError(e.response?.data?.error || L.booster.addonFailed);
			setShowAddons(false);
		} finally {
			setAddonLoading(false);
		}
	};

	// The extras implied by the remembered configuration, used to pre-tick the
	// popup. The three add-on keys and the three remembered options are the same
	// three things, so a returning customer confirms rather than re-answers.
	const rememberedAddons = () => {
		if (selectedPack) return [];
		return [optFast && 'fast', optFemale && 'female', optUsa && 'usa'].filter(Boolean);
	};

	// The line items sitting on top of the base boost: the grade upcharge, then
	// each chosen extra. Derived from the same values confirmTotal sums, so the
	// itemisation can never disagree with the figure being charged.
	const upcharges = () => {
		const base = Number(price) || 0;
		const rows = [];
		const graded = qualityPrice(base, quality);
		if (graded > base) {
			rows.push({ label: L.fiftyoff.quality.tiers[quality]?.name || quality, amount: graded - base });
		}
		const each = addonPriceFor(base);
		chosenAddons.forEach((k) => rows.push({ label: L.booster.addons[k].title, amount: each }));
		return rows;
	};

	// What the confirm step will charge: the boost at the chosen grade, plus any
	// extras. The grade belongs in here — the order is charged at the graded
	// price (applyQuality in routes/boosts.js), so a total that ignored it
	// quoted less than the customer was about to pay.
	const confirmTotal = () => {
		const base = selectedPack ? selectedPack.price : Number(price) || 0;
		const graded = selectedPack ? base : qualityPrice(base, quality);
		return (graded + chosenAddons.length * addonPriceFor(base)).toFixed(2);
	};

	const [prices, setPrices] = useState([]);
	const [statuses, setStatuses] = useState([]);
	const getPrices = async () => {
		try {
			const response = await axios.get("https://api.viralgpt.com/boosts/prices");
			setPrices(response.data);
			setStatuses(response.data)
		} catch (e) {
			console.error("Failed to get prices: " + e);
		}
	}

	useEffect(() => {
		getPrices();
	}, []);

	const [loadingPage, setLoadingPage] = useState(false);
	const [loadingPosts, setLoadingPosts] = useState(false);
	const getPosts = async (page) => {
		try {
			if (page == 0) {
				setLoadingPosts(true)
				setCurrentPage(0)
			}
			var cursor = page === 0 ? 0 : cursors[page - 1];
			setLoadingPage(true)
			const response = await axios.post(`https://api.viralgpt.com/boosts/${service === "views" && platform !== "tiktok" ? "reels" : "posts"}`, {
				"username": postLink,
				"platform": platform,
				"cursor": cursor
			}, {
				headers: {
					"Authorization": `Bearer ${localStorage.getItem("accessToken")}`
				}
			});
			setPosts(response.data.posts);
			setCursors(cursors => ({ ...cursors, [page]: response.data.cursor }));
		} catch (e) {
			console.error("Failed to get posts: " + e);
			setFunctionError(L.booster.noPosts);
		} finally {
			setLoadingPosts(false)
			setLoadingPage(false)
		}
	}




	// What a quantity costs. Followers price off a curve that matches the packages
	// sold at ad checkout — same price for the same size, wherever it is bought —
	// and it arrives with the prices payload rather than being copied here, so
	// the slider cannot drift from what the server bills. Everything else is a
	// flat rate per 1,000.
	const catalogPrice = (entry, qty) => {
		const q = Number(qty) || 0;
		const tiers = entry?.tiers;
		if (!tiers?.anchors?.length) return ((entry?.price || 0) / 1000) * q;
		if (q <= 0) return 0;
		let prevQty = 0;
		let prevPrice = 0;
		for (const a of tiers.anchors) {
			if (q <= a.qty) return prevPrice + (q - prevQty) * ((a.price - prevPrice) / (a.qty - prevQty));
			prevQty = a.qty;
			prevPrice = a.price;
		}
		return (q / 1000) * tiers.overflowPerK;
	};

	// Dashboard packages use the same 50%-off ladder as the public funnel.
	// Keep the original amount derived from the live price, rather than copying a
	// second price table into the UI: $4.99 becomes $9.99, $79.99 becomes $159.99.
	const regularPackagePrice = (salePrice) => {
		const cents = Math.round((Number(salePrice) || 0) * 100);
		if (cents <= 0) return 0;
		return (Math.ceil((cents * 2) / 100) * 100 - 1) / 100;
	};

	useEffect(() => {
		const updatePrice = async () => {
			let selectedValue;
			if (service === "likes") selectedValue = likesValue;
			if (service === "views") selectedValue = viewsValue;
			if (service === "comments") selectedValue = commentsValue;
			if (service === "followers") selectedValue = followersValue;

			const serviceKey = platform + "-" + service;
			// Not every platform carries every service (there is no x-comments row,
			// for one). Reading .min off a missing entry threw and blanked the tab —
			// harmless when service led the layout, reachable now that platform does.
			if (!prices[serviceKey]) {
				setPrice(0);
				setNormalPrice(0);
				setQuantity(0);
				return;
			}
			setMin(prices[serviceKey].min);

			{

				// One rate for everyone — no plan buys a cheaper catalog; the balance
				// memberships are discounted through their monthly grant instead.
				// The server re-derives this and is what actually charges.
				const shown = catalogPrice(prices[serviceKey], selectedValue).toFixed(2);
				setPrice(shown);
				setNormalPrice(shown);
				setQuantity(selectedValue);
			}
		}
		if (Object.keys(prices).length > 0) {
			updatePrice();
		}
	}, [likesValue, viewsValue, commentsValue, followersValue, service, platform, prices, profile.planName]);

	// Two passes, because the order list used to wait on a JAP round trip that
	// asks the panel about every order the customer has ever placed — so the
	// longer someone had been a customer, the longer they stared at an empty
	// table. ?fast=1 skips that call and returns our own rows, which is
	// everything except live progress; the full request follows immediately and
	// fills in start_count/remains (and runs the server's refund sweep, which
	// needs the live status). The rows are the same shape either way, so the
	// second response just replaces the first.
	useEffect(() => {
		let cancelled = false;
		const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } };

		const getOrders = async () => {
			if (!localStorage.getItem("accessToken")) return;

			// Paint from our own data first. A failure here is not surfaced — the
			// full request below is the one that has to work.
			const quick = axios
				.get("https://api.viralgpt.com/boosts/order/status?fast=1", auth)
				.then((r) => {
					// Don't overwrite the full response if it somehow won the race.
					if (!cancelled && !liveOrdersRef.current) setOrders(r.data);
				})
				.catch(() => {});

			try {
				const response = await axios.get("https://api.viralgpt.com/boosts/order/status", auth);
				if (cancelled) return;
				liveOrdersRef.current = true;
				setOrders(response.data);
				setOrdersLiveLoaded(true);
			} catch (err) {
				console.error("Order history failed to load:", err);
			}
			await quick;
		};

		liveOrdersRef.current = false;
		setOrdersLiveLoaded(false);
		getOrders();
		return () => { cancelled = true; };
	}, [orderTable]);


	// Switching platform can strip the selected service (no x-comments row), which
	// would leave the form pointing at nothing purchasable.
	useEffect(() => {
		if (Object.keys(prices).length === 0) return;
		if (!prices[platform + '-' + service]) {
			const fallback = ['followers', 'likes', 'views', 'comments'].find((k) => prices[platform + '-' + k]);
			if (fallback) setService(fallback);
		}
	}, [platform, service, prices]);

	useEffect(() => {
		setCurrentPage(0)
		setCursors({ 0: 0 })
		setPosts([])
		setSelectedPost(null)
		setSelectedPosts([])
	}, [postLink, platform, service]);

	const isDisabled = (!selectedPost && (platform === "instagram" || platform === "tiktok") && service !== "followers") || prices[platform + "-" + service]?.enabled === 0;

	// Switching platform or service starts a new order. The size and any bundle
	// belonged to the tab that was on screen: carrying them across kept the
	// sticky footer up over a selection the customer could no longer see, and on
	// a platform switch silently repriced it against a catalog they never picked
	// it from. Nothing seeds these from the profile or a past order — every other
	// writer is a click — so clearing them loses nothing but the stale choice.
	useEffect(() => {
		setSelectedPack(null);
		setFollowersValue(0);
		setLikesValue(0);
		setViewsValue(0);
		// commentsValue counts the lines in the textarea, so the two reset together
		// or the summary quotes a total for text that is no longer there.
		setCommentsValue(0);
		setComments("");
		setCustomFollowers(false);
		setCustomLikes(false);
		setCustomViews(false);
	}, [platform, service]);

	// On X and YouTube a likes/views order names a post, not an account — the
	// popup's question and the lookup both have to know which they are asking
	// about. YouTube followers take a channel URL, so no handle preview there.
	const isPostLinkInput = (platform === 'x' || platform === 'youtube') && service !== 'followers';
	const canPreviewProfile = !isPostLinkInput && platform !== 'youtube';

	// Accepts what people actually paste — @handle, bare handle, or a profile
	// URL — and returns the bare handle the lookup and the order both want.
	const normalizeHandle = (raw) => {
		const str = String(raw || '').trim();
		const m = str.match(/(?:instagram\.com|tiktok\.com|x\.com|twitter\.com)\/@?([A-Za-z0-9._]+)/i);
		return (m ? m[1] : str).replace(/^@/, '');
	};

	// No handle means no destination. Checked for every service, including
	// followers, which needs no post and so passed every other gate.
	const needsProfile = !String(postLink || '').trim();

	// Blocked only for want of a post — as opposed to the service being switched
	// off, which nothing the customer does will fix.
	const needsPost = isDisabled && prices[platform + "-" + service]?.enabled !== 0;

	// Where the handle is typed and the posts are picked. Both sit below the
	// quantity picker, so on likes and views the CTA was sitting disabled while
	// the step it waited on was off-screen — the button now leads there instead.
	const accountRef = useRef(null);
	const postsRef = useRef(null);
	const startPostFlow = () => {
		if (needsProfile) { setShowProfileModal(true); return; }
		else if (posts.length === 0 && !loadingPosts) getPosts(0);
		const target = posts.length > 0 ? postsRef.current : accountRef.current;
		target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	};

	// Opens the confirm step. Nothing is charged until Pay in the popup — the
	// add-ons change the total, so the customer has to see the final figure
	// before any money moves. Shared by the summary CTA and the sticky bar so
	// the two can never take different paths to checkout.
	// Look the handle up as it is typed. Sequenced so a slow response for an
	// earlier spelling can never overwrite the preview of the current one.
	useEffect(() => {
		if (!showProfileModal || !canPreviewProfile) return;
		const handle = normalizeHandle(postLink);
		setProfilePreview(null);
		setPreviewFailed(false);
		if (!handle) { setPreviewLoading(false); return; }
		setPreviewLoading(true);
		const seq = ++previewSeq.current;
		if (previewTimer.current) clearTimeout(previewTimer.current);
		previewTimer.current = setTimeout(async () => {
			try {
				const r = await axios.get(`https://api.viralgpt.com/auth/getProfile/${platform}/${encodeURIComponent(handle)}`);
				if (seq !== previewSeq.current) return;
				const ok = r.data && r.data.username;
				setProfilePreview(ok ? r.data : null);
				setPreviewFailed(!ok);
			} catch (e) {
				if (seq !== previewSeq.current) return;
				setProfilePreview(null);
				setPreviewFailed(true);
			} finally {
				if (seq === previewSeq.current) setPreviewLoading(false);
			}
		}, 700);
		return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postLink, showProfileModal, platform, service]);

	const confirmProfile = () => {
		if (!String(postLink).trim()) return;
		// The order ships what this field holds, so a pasted profile URL is
		// reduced to the handle before it goes anywhere. Post-URL flows keep the
		// URL — the URL is the thing being boosted. The lookup's canonical
		// username wins when it resolved, so casing and trailing dots match the
		// account that was previewed.
		if (canPreviewProfile) setPostLink(profilePreview?.username || normalizeHandle(postLink));
		setShowProfileModal(false);
		// Likes and views need posts next, so load them rather than making the
		// customer press the same button a second time to get there.
		if (showSearchBtn(service) && posts.length === 0 && !loadingPosts) getPosts(0);
	};

	const openConfirmStep = () => {
		// Belt and braces: the CTAs route to the popup themselves, but nothing
		// reaches the charge without a handle.
		if (needsProfile) { setShowProfileModal(true); return; }
		if (!(selectedPack || !isDisabled)) return;
		setChosenAddons(rememberedAddons());
		setShowAddons(true);
	};

	// What the CTA does next: collect the handle, then the posts, then charge.
	// Both buttons read this, so neither can offer a step the other doesn't.
	const ctaAction = needsProfile
		? () => setShowProfileModal(true)
		: needsPost ? startPostFlow : openConfirmStep;
	const ctaPendingText = needsProfile ? L.booster.chooseProfile : needsPost ? L.booster.loadPosts : null;
	// Seed the remembered configuration once the profile has loaded. Seeded once
	// rather than on every render, so an edit is not undone by the next refresh.
	useEffect(() => {
		if (optsSeeded) return;
		const social = profile?.socials?.[profile?.selectedProfile];
		if (!social) return;
		let ups = [];
		try { ups = JSON.parse(social.upsells || '[]') || []; } catch (e) { ups = []; }
		if (QUALITY_TIERS.some((t) => t.key === social.quality)) setQuality(social.quality);
		setOptUsa(social.countries === 'us' || ups.includes('usa'));
		setOptFemale(social.genderTarget === 'women' || ups.includes('female'));
		setOptFast(ups.includes('fast'));
		setOptsSeeded(true);
	}, [profile?.selectedProfile, profile?.socials, optsSeeded]);


	const followerTiers = FOLLOWER_TIERS;

	const hasValue = !!selectedPack || followersValue > 0 || likesValue > 0 || viewsValue > 0 || commentsValue > 0;

	// The sticky footer follows the selection on the tab being looked at: a
	// bundle, or a size for this service. On likes and views that selection still
	// needs a post, and the footer carries the way to choose one rather than
	// hiding until the customer finds that step themselves.
	//
	// Keyed on `quantity`, which is the selected size of the service being shown,
	// NOT hasValue — that is true if any service holds a value, so a size picked
	// on Likes kept the footer up after switching to Followers, quoting the new
	// tab's empty quantity and $0.00.
	//
	// A switched-off service is the one case with nothing to offer: no post to
	// choose and nothing to buy, so the bar stays down instead of showing a dead
	// button.
	const stickyReady = (!!selectedPack || Number(quantity) > 0)
		&& prices[platform + "-" + service]?.enabled !== 0;
	const stickyBusy = selectedPack ? packLoading : pendingSend;
	const shouldHideCrisp = !!selectedPack || stickyReady;

	// The chat launcher otherwise sits directly on top of the sticky checkout
	// bar. Keep it out of the way from the moment a bundle is selected, and
	// restore it as soon as the customer clears their selection or leaves.
	useEffect(() => {
		if (window.$crisp) window.$crisp.push(['do', shouldHideCrisp ? 'chat:hide' : 'chat:show']);
		return () => {
			if (window.$crisp) window.$crisp.push(['do', 'chat:show']);
		};
	}, [shouldHideCrisp]);

	const sliderTrack = (props, state) => (
		<div
			{...props}
			style={{
				...props.style,
				background: state.index === 0
					? 'linear-gradient(90deg, #635bff, #8b5cf6)'
					: '#e2e8f0',
			}}
		/>
	);

	const serviceTabs = [
		{ key: 'followers', icon: 'fa-users', label: L.booster.services.followers },
		{ key: 'likes', icon: 'fa-heart', label: L.booster.services.likes },
		{ key: 'views', icon: 'fa-eye', label: L.booster.services.views },
		{ key: 'comments', icon: 'fa-comment', label: L.booster.services.comments },
	];

	const platforms = [
		{ key: 'instagram', icon: 'fab fa-instagram', label: 'Instagram', color: '#E1306C' },
		{ key: 'tiktok', icon: 'fab fa-tiktok', label: 'TikTok', color: '#000' },
		{ key: 'youtube', icon: 'fab fa-youtube', label: 'YouTube', color: '#FF0000' },
		{ key: 'x', icon: null, label: 'Twitter', color: '#000', customIcon: '𝕏' },
	];

	// 14px each side across nine columns was most of what pushed the table past a
	// laptop width; the rows read the same at 10.
	const thStyle = { padding: '10px 10px', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#68778d', borderBottom: '1px solid #e3e8ee', background: '#f6f9fc' };
	const tdStyle = { padding: '12px 10px', fontSize: '0.82rem', color: '#0a2540', borderBottom: '1px solid #f1f5f9' };
	const tdMuted = { ...tdStyle, color: '#68778d' };

	// A package or upsell delivery costs 0 balance — it was paid by card at
	// checkout — so the amount comes from the charge (paid_amount), not the row.
	const orderPrice = (order) => {
		if (Number(order.price) > 0) return `$${order.price}`;
		if (order.paid_amount != null) return `$${Number(order.paid_amount).toFixed(2)}`;
		return L.booster.paidAtCheckout;
	};

	// boost_name carries the customer-facing label ("Boost Engagement+"); the
	// service key only says "instagram-views", which reads as a view counter
	// rather than the pack they bought.
	const serviceName = (order) => {
		if (order.boost_name && !/^[a-z]+-[a-z]+$/.test(order.boost_name)) return order.boost_name;
		const part = String(order.boost_type || '').split('-')[1] || '';
		return part.charAt(0).toUpperCase() + part.slice(1);
	};

	// The delivery banner should name the purchase, not just say that an order is
	// moving. Build it from the stable type fields so a custom boost_name cannot
	// duplicate the quantity (for example, "1,200 1,200 Followers").
	// Provider quantities can be adjusted slightly to balance a pack across
	// posts. Keep that implementation detail out of the customer-facing banner:
	// 14,988 should read as the 15k pack they chose.
	const deliveryQuantity = (quantity) => {
		if (quantity === null || quantity === undefined || quantity === '') return '—';
		const value = Number(quantity) || 0;
		if (value >= 10000) return `${Math.round(value / 1000)}k`;
		return value.toLocaleString();
	};
	const deliveryOrderName = (order) => {
		const [platformKey, serviceKey] = String(order?.boost_type || '').split('-');
		const platformName = platforms.find((item) => item.key === platformKey)?.label
			|| (platformKey ? platformKey.charAt(0).toUpperCase() + platformKey.slice(1) : '');
		const serviceLabel = serviceTabs.find((item) => item.key === serviceKey)?.label
			|| (serviceKey ? serviceKey.charAt(0).toUpperCase() + serviceKey.slice(1) : serviceName(order));
		return `${deliveryQuantity(order?.quantity)} ${platformName} ${String(serviceLabel).toLowerCase()}`.trim();
	};

	// 0 is meaningful here ("0 remaining" = fully delivered), so only null and
	// undefined fall back to the placeholder.
	const num = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString());

	// Every order has useful core information (service, account, quantity, price
	// and date). Keep its details view available even when an old profile was
	// deleted and its saved targeting preferences are no longer present.
	const hasDetails = () => true;

	// Speed and targeting are preferences bought with the package rather than
	// separate orders, so they live behind a details view instead of adding rows.
	const orderPrefs = (order) => {
		const p = order.order_prefs;
		let snapshot = order.order_options;
		try { if (typeof snapshot === 'string') snapshot = JSON.parse(snapshot); } catch (e) { snapshot = null; }
		if (!p && !snapshot) return null;
		let keys = [];
		if (Array.isArray(snapshot?.addons)) keys = snapshot.addons;
		else try { keys = JSON.parse(p?.upsells || '[]') || []; } catch (e) { /* malformed — fall back to defaults */ }
		const V = L.visualizer;
		if (Array.isArray(snapshot?.line_items)) {
			const labelFor = (key) => {
				if (key === 'base') return L.booster.baseFollowers;
				if (key.startsWith('quality:')) return L.fiftyoff.quality.tiers[key.split(':')[1]]?.name || key;
				if (key.startsWith('addon:')) return L.booster.addons[key.split(':')[1]]?.title || key;
				if (key.startsWith('upsell:')) return L.booster.upsellLabels?.[key.split(':')[1]] || key.split(':')[1];
				return key;
			};
			return snapshot.line_items.map((item, index) => [
				labelFor(item.key),
				`${index === 0 ? '' : '+'}$${Number(item.amount || 0).toFixed(2)}`,
			]);
		}
		const qualityKey = snapshot?.quality || p?.quality || DEFAULT_QUALITY;
		const qualityLabel = L.fiftyoff.quality.tiers[qualityKey]?.name || qualityKey;
		const basePrice = Number(snapshot?.base_price);
		const qualityPricePaid = Number(snapshot?.quality_price ?? order.price);
		const qualityExtra = Number.isFinite(basePrice) && Number.isFinite(qualityPricePaid)
			? Math.max(0, qualityPricePaid - basePrice) : null;
		const withExtra = (label, amount) => amount > 0 ? `${label} (+$${amount.toFixed(2)})` : label;

		// Always all three, defaults included. Showing only the non-default ones
		// meant an order left on standard settings had nothing to open, so the
		// customer couldn't see what they were actually getting.
		const speed = keys.includes('fast') ? V.speedFast : V.speedGradual;

		const gender = keys.includes('female')
			? V.audienceWomen
			: p?.genderTarget === 'women' ? V.audienceWomen
			: p?.genderTarget === 'men' ? V.audienceMen
			: V.audienceEveryone;

		const country = keys.includes('usa') || p?.countries === 'us'
			? V.locationUsa
			: (p?.countries && p.countries !== 'global')
				? (L.magnet?.countries?.[p.countries] || String(p.countries).toUpperCase())
				: V.locationWorldwide;
		const addonEach = Number(snapshot?.addon_price_each);
		const rows = [
			[L.booster.prefQuality, withExtra(qualityLabel, qualityExtra)],
			[L.booster.prefDelivery, speed],
			[L.booster.prefTargeting, gender],
			[L.booster.prefLocation, country],
		];
		if (Number.isFinite(addonEach)) {
			return rows.map(([label, value]) => {
				const paidAddon = (label === L.booster.prefDelivery && keys.includes('fast'))
					|| (label === L.booster.prefTargeting && keys.includes('female'))
					|| (label === L.booster.prefLocation && keys.includes('usa'));
				return [label, paidAddon ? withExtra(value, addonEach) : value];
			});
		}
		return rows;
	};

	// "instagram.com/foo" / "…/p/ABC123" rather than the whole URL. Named
	// orderLinkLabel to avoid colliding with linkLabel(), which labels the input
	// field for a service.
	const orderLinkLabel = (url) => {
		if (!url) return '';
		return String(url).replace(/^https?:\/\//, '').replace(/^www\./, '');
	};

	// Dispatch an order that was paid for but never delivered. Nothing is charged
	// — the money was taken at purchase; this only sends what it already bought.
	// A private account is refused server-side before anything reaches the panel,
	// and comes back here as the one thing the customer can actually fix.
	// `username` is only sent from the USERNAME_INVALID row's input. Omitted, this
	// is the plain Send it always was.
	const sendOrder = async (order, username) => {
		if (!order?.id || sendingPackId) return false;
		setSendingPackId(order.id);
		try {
			await axiosClient.post('https://api.viralgpt.com/purchase/order/resend', {
				boostId: order.id,
				...(username ? { username } : {}),
			});
			// Reload so the row picks up its panel order id and live progress. A new
			// value each time, since this is the order-list effect's dependency.
			updateOrderTable(Date.now());
			return true;
		} catch (err) {
			const code = err.response?.data?.error;
			window.alert({
				account_private: L.booster.packNeedsPublic,
				account_missing: L.booster.packBadUsername,
				username_malformed: L.booster.packUsernameMalformed,
				username_required: L.booster.packUsernameRequired,
				check_unavailable: L.booster.packCheckUnavailable,
			}[code] || L.booster.packSendFailed);
			return false;
		} finally {
			setSendingPackId(null);
		}
	};

	// Which account an order was for, and where to go to see it. The stored link
	// is the profile url for follower and pack orders, but a post url for a boost
	// bought against a single post — so a handle is only read out of it when the
	// url is actually a profile, and otherwise comes from the linked account for
	// that platform. Returns null rather than guessing when neither is known.
	const orderAccount = (order) => {
		const platform = String(order?.boost_type || '').split('-')[0];
		const url = String(order?.link || '');
		const path = url.replace(/^https?:\/\/(www\.)?[^/]+\//, '').replace(/\/+$/, '');
		// /p/, /reel/ and /status/ are posts, not people.
		const isProfileUrl = path && !path.includes('/');
		const socials = profile?.socials || {};
		const linked = Object.values(socials).find((x) => x?.platform === platform && x?.username);
		const handle = (isProfileUrl ? path.replace(/^@/, '') : '') || linked?.username || '';
		if (!handle) return null;

		const profileUrl = {
			instagram: `https://instagram.com/${handle}`,
			tiktok: `https://tiktok.com/@${handle}`,
			x: `https://x.com/${handle}`,
			youtube: `https://youtube.com/@${handle}`,
		}[platform] || (isProfileUrl ? url : '');
		return profileUrl ? { handle, profileUrl } : null;
	};

	// Orders paid for but never delivered, waiting on the customer to make their
	// account public. Deliberately only awaiting_public: refunded/canceled/failed
	// are resendable too, but they are historical rather than blocked on anything
	// the customer can do today, and banner-ing those would nag most of the base
	// about orders from months ago.
	const pendingSends = orders.filter(
		(o) => String(o.v_status || o.status || '').toLowerCase() === 'awaiting_public'
	);
	// Show the delivery notice from the source of truth (the current order
	// status), not browser storage. That covers orders made before this UI
	// shipped and orders viewed from a different device, and it naturally goes
	// away as soon as the provider reports completion.
	const deliveryInProgress = ordersLiveLoaded && orders.find((order) => {
		const status = String(order.v_status || order.status || '').toLowerCase();
		return status === 'creating' || status === 'queued' || status === 'pending'
			|| status === 'processing' || status.includes('progress');
	});
	const deliveryStatusLabel = (order) => {
		const status = String(order?.v_status || order?.status || '').toLowerCase();
		if (status === 'creating' || status === 'queued' || status === 'pending') return L.booster.deliveryStarting;
		if (status === 'processing' || status.includes('progress')) return L.booster.deliveryInProgress;
		return status || L.booster.deliveryStarting;
	};

	// The history sits far below the fold, so the banner has to carry the visitor
	// to it rather than just describe where it is.
	const scrollToOrders = () => ordersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

	// Names what the order actually ships. boost_type is
	// "<platform>-<service>[-variant]", so the middle segment is the service —
	// which also reads 'followers' for 'instagram-followers-instant'.
	const sendLabelFor = (order) => {
		const service = String(order.boost_type || '').split('-')[1];
		return L.booster.packSendBy?.[service] || L.booster.packSend;
	};

	// Takes the place of the status badge on an undelivered order. `block` is the
	// mobile card's variant: full width and its own row, because squeezed beside
	// the service name it read as a status chip rather than something to press.
	const SendButton = ({ order, block = false }) => (
		<button
			type="button"
			onClick={(e) => { e.stopPropagation(); sendOrder(order); }}
			disabled={sendingPackId === order.id}
			style={{
				display: block ? 'flex' : 'inline-flex',
				alignItems: 'center', justifyContent: 'center', gap: 6,
				width: block ? '100%' : 'auto',
				padding: block ? '11px 16px' : '5px 12px',
				borderRadius: block ? 10 : 6, border: 'none',
				cursor: sendingPackId === order.id ? 'default' : 'pointer',
				background: '#635bff', color: '#fff',
				fontSize: block ? '0.88rem' : '0.72rem', fontWeight: 700,
				whiteSpace: 'nowrap',
				boxShadow: '0 2px 8px rgba(99,91,255,0.35)',
				opacity: sendingPackId === order.id ? 0.6 : 1,
			}}
		>
			{sendingPackId !== order.id && <i className="fa-solid fa-paper-plane" style={{ fontSize: block ? '0.8rem' : '0.65rem' }}></i>}
			{sendingPackId === order.id ? L.booster.packSending : sendLabelFor(order)}
		</button>
	);

	// Takes the place of Send when the stored handle does not resolve. Waiting
	// cannot fix this one — 69% of panel cancellations are a dead or malformed
	// username, a fifth of those a display name typed into the field — so the row
	// asks for the missing piece instead of offering a button that would fail
	// again. Local state per row: the customer is correcting their own typo, and
	// hoisting it would clear the box on every order-table refresh.
	const UsernameFixer = ({ order, block = false }) => {
		const [value, setValue] = useState('');
		const busy = sendingPackId === order.id;
		const submit = async () => {
			const handle = value.trim().replace(/^@/, '');
			if (!handle || busy) return;
			if (await sendOrder(order, handle)) setValue('');
		};
		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: block ? 'auto' : 190 }}>
				<span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#b45309' }}>
					{L.booster.packUsernamePrompt}
				</span>
				<div style={{ display: 'flex', gap: 4 }}>
					<input
						type="text"
						value={value}
						onClick={(e) => e.stopPropagation()}
						onChange={(e) => setValue(e.target.value)}
						onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); submit(); } }}
						placeholder={L.booster.packUsernamePlaceholder}
						disabled={busy}
						style={{
							flex: 1, minWidth: 0, padding: '5px 8px', borderRadius: 6,
							border: '1px solid #fcd34d', background: '#fffbeb',
							fontSize: '0.72rem', outline: 'none',
						}}
					/>
					<button
						type="button"
						onClick={(e) => { e.stopPropagation(); submit(); }}
						disabled={busy || !value.trim()}
						style={{
							padding: '5px 10px', borderRadius: 6, border: 'none',
							background: '#635bff', color: '#fff',
							fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap',
							cursor: busy || !value.trim() ? 'default' : 'pointer',
							opacity: busy || !value.trim() ? 0.6 : 1,
						}}
					>
						{busy ? L.booster.packSending : L.booster.packUsernameSave}
					</button>
				</div>
				<span style={{ fontSize: '0.62rem', color: '#94a3b8', lineHeight: 1.3 }}>
					{L.booster.packUsernameHelp}
				</span>
			</div>
		);
	};

	// Status arrives in three different casings and this has to match all of
	// them: the panel returns "Completed" / "In progress" / "Pending", the
	// aggregate in routes/boosts.js re-capitalizes what it rolls up, and rows we
	// write ourselves are lowercase ('queued'). Comparing case-sensitively meant
	// a finished order fell through to the gray fallback and rendered "Completed"
	// in the same grey as an unknown state.
	// Orders that were paid for but never actually delivered. "Refunded" is the
	// misleading one: the auto-sweep stamps it when the panel cancels an order,
	// but a package row carries price 0 — it was paid by card, not balance — so
	// nothing was credited back. It is not money returned, it is an order still
	// owed. These show a Send button in place of a status nobody can act on.
	const RESENDABLE = ['awaiting_public', 'username_invalid', 'refunded', 'canceled', 'failed'];
	const isResendable = (order) => RESENDABLE.includes(String(order.v_status || '').toLowerCase());
	// Read from the stored status as well as the live one: this state is ours, not
	// the panel's, so v_status can be blank for it.
	// EITHER field, not `v_status || status`. That short-circuit read only the
	// live value, and the two disagree on exactly the rows this matters for: the
	// panel reports 'canceled', while 'username_invalid' — a state of ours the
	// panel knows nothing about — sits in the stored column. The result was a
	// plain Send button on a row the backend then refused with username_required.
	const needsUsername = (order) =>
		[order.v_status, order.status].some((s) => String(s || '').toLowerCase() === 'username_invalid');

	const getStatusStyle = (order) => {
		// v_status only, with `status` as a fallback for rows that have none.
		// Matching either field independently let the colour come from the live
		// panel status while the label came from the stored one, so a finished
		// order rendered the word "Pending" on a green badge. The badge must
		// describe the text next to it.
		const s = String(order.v_status || order.status || '').toLowerCase();
		const is = (...names) => names.some((n) => s === n);

		if (is('awaiting_public', 'username_invalid')) return { bg: '#fef3c7', color: '#b45309' };
		if (is('completed')) return { bg: '#dcfce7', color: '#16a34a' };
		// 'queued' = charged, not yet accepted by the panel. Reads as Pending to
		// the customer; it is what the redelivery script picks up.
		if (is('queued', 'pending')) return { bg: '#fef9c3', color: '#a16207' };
		if (is('processing') || s.includes('progress')) return { bg: '#dbeafe', color: '#2563eb' };
		return { bg: '#f1f5f9', color: '#64748b' };
	};

	// The fast history response deliberately skips the panel status call. Show a
	// neutral spinner during that short window instead of briefly presenting a
	// stale "Pending" badge before the live response says Completed.
	const OrderStatus = ({ order, mobile = false }) => {
		if (!ordersLiveLoaded) return <i className="fas fa-spinner fa-spin" aria-label="Checking delivery status" style={{color: '#94a3b8', fontSize: mobile ? '0.75rem' : '0.8rem'}}></i>;
		// The fixer has to render on mobile too: unlike Send, which the mobile card
		// repeats as a full-width button lower down, this is the only way to act on
		// the row and hiding it would leave the order stuck with no visible cause.
		if (needsUsername(order)) return <UsernameFixer order={order} block={mobile} />;
		if (isResendable(order)) return mobile ? null : <SendButton order={order} />;
		const st = getStatusStyle(order);
		const rawStatus = String(order.v_status || order.status || '').toLowerCase();
		const statusLabel = ['creating', 'queued', 'pending'].includes(rawStatus)
			? L.booster.deliveryStarting
			: rawStatus === 'processing' || rawStatus.includes('progress')
				? L.booster.deliveryInProgress
				: rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);
		return (
			<span style={{padding: '3px 8px', borderRadius: '6px', fontSize: mobile ? '0.68rem' : '0.72rem', fontWeight: 600, background: st.bg, color: st.color}}>
				{statusLabel}
			</span>
		);
	};

	const linkPlaceholder = (svc) => {
		if (svc === "followers") {
			return platform === "youtube" ? `https://youtube.com/channel/<channel_id>` : L.booster.enterUsername(platform.charAt(0).toUpperCase() + platform.slice(1));
		}
		return platform === "youtube" ? "https://youtube.com/watch?v=<video_id>" :
			platform === "x" ? "https://twitter.com/<username>/status/<tweet_id>" :
				platform === "instagram" ? L.booster.enterUsername("Instagram") :
					platform === "tiktok" ? L.booster.enterUsername("TikTok") : L.booster.enterUsernameDefault;
	};

	const linkLabel = (svc) => {
		if (svc === "followers") {
			return platform === "youtube" ? L.booster.youtubeProfileLink : L.booster.usernameLabel(platform.charAt(0).toUpperCase() + platform.slice(1));
		}
		return (platform === "x" || platform === "youtube") ? L.booster.postLinkLabel(platform.charAt(0).toUpperCase() + platform.slice(1)) : L.booster.usernameLabel(platform.charAt(0).toUpperCase() + platform.slice(1));
	};

	const showSearchBtn = (svc) => {
		if (svc === "followers") return false;
		return platform !== "x" && platform !== "youtube";
	};

	return (
		<>
			<Balance
				show={balanceModal}
				onHide={() => { setBalanceModal(false); setResumeAfterTopup(false); }}
				profile={profile}
				onSuccess={async (added) => {
					setBalanceModal(false);
					if (!resumeAfterTopup) { window.location.reload(); return; }
					setResumeAfterTopup(false);
					// The balance the server holds is what matters; the local copy is
					// only so the summary doesn't show a stale figure mid-flight.
					profile.balance = (Number(profile.balance) || 0) + Number(added || 0);
					setFunctionError("");
					try {
						if (selectedPack) await buyEngagementPack(selectedPack);
						else await handleStartDelivery(service);
					} catch (e) {
						setFunctionError(e.response?.data?.error || L.booster.failedCreateOrder(""));
					}
				}}
			/>
			<Container className="py-3 px-2 col-md-8">
				<Alert variant="danger" message={functionError} show={!!functionError} onClose={() => setFunctionError("")} />
				<Alert variant="success" message={functionSuccess} show={!!functionSuccess} onClose={() => setFunctionSuccess("")} />
				{deliveryInProgress && (
					<div style={{background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '12px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px'}}>
						<i className="fa-solid fa-truck-fast" style={{color: '#16a34a', fontSize: '1rem'}}></i>
						<div style={{minWidth: 0}}>
							<div style={{fontWeight: 700, color: '#166534', fontSize: '0.86rem', lineHeight: 1.3}}>
								{L.booster.deliveryInProgressTitle(deliveryOrderName(deliveryInProgress))}
							</div>
							<div style={{color: '#15803d', fontSize: '0.74rem', fontWeight: 600, marginTop: '2px'}}>
								{L.booster.deliveryStatus(deliveryStatusLabel(deliveryInProgress))}
							</div>
						</div>
					</div>
				)}

				<Tab.Container activeKey={service}>
					{/* Room for the sticky bar, so it can't sit on top of the last card. */}
					<div style={{display: 'flex', flexDirection: 'column', gap: '16px', paddingBottom: stickyReady ? 'calc(84px + env(safe-area-inset-bottom))' : 0}}>

						{/* An undelivered order is invisible unless the customer scrolls to
						    the history, so the dashboard says so up front and takes them
						    there. */}
						{pendingSends.length > 0 && (
							<div className="vg-pending-banner" style={{
								background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px',
								padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: '12px',
							}}>
								<i className="fa-solid fa-triangle-exclamation" style={{color: '#b45309', fontSize: '1rem', marginTop: '2px'}}></i>
								<div style={{flex: 1, minWidth: 0}}>
									<div style={{fontWeight: 700, color: '#92400e', fontSize: '0.88rem'}}>
										{L.booster.pendingSendTitle(pendingSends.length)}
									</div>
									<div style={{color: '#b45309', fontSize: '0.8rem', marginTop: '2px'}}>
										{L.booster.pendingSendBody}
									</div>
									<button
										type="button"
										onClick={scrollToOrders}
										style={{
											marginTop: '8px', background: 'none', border: 'none', padding: 0,
											color: '#92400e', fontWeight: 700, fontSize: '0.8rem',
											cursor: 'pointer', textDecoration: 'underline',
										}}
									>
										{L.booster.pendingSendCta} <i className="fa-solid fa-arrow-down" style={{fontSize: '0.7rem'}}></i>
									</button>
								</div>
							</div>
						)}

						{/* Main card */}
						<div style={{background: '#fff', borderRadius: '12px', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)', overflow: 'hidden'}}>

							{/* Platform tabs. Platform is the broader choice — which account
							    are we boosting — so it leads, and the service picker below
							    changes meaning within it. */}
							<div className="vg-platform-tabs" style={{display: 'flex', borderBottom: '1px solid #e3e8ee', overflowX: 'auto'}}>
								{platforms.map(p => (
									<button key={p.key} onClick={() => setPlatform(p.key)} style={{
										flex: 1, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
										fontWeight: 600, transition: 'all 0.15s ease', whiteSpace: 'nowrap', minWidth: 0,
										background: platform === p.key ? '#f5f3ff' : '#fff',
										color: platform === p.key ? '#635bff' : '#68778d',
										borderBottom: platform === p.key ? '2px solid #635bff' : '2px solid transparent',
									}}>
										{p.customIcon
											? <span style={{fontWeight: 800, fontSize: '0.9rem'}}>{p.customIcon}</span>
											: <i className={`${p.icon} ${brandClass(p.key)}`} style={{fontSize: '0.95rem'}}></i>}
										{p.label}
									</button>
								))}
							</div>

							{/* Service pills */}
							<div style={{padding: '16px 16px 0'}}>
								<label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', display: 'block'}}>{L.booster.serviceLabel}</label>
								<div className="vg-service-pills" style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
									{serviceTabs
										.filter((tab) => Object.keys(prices).length === 0 || prices[platform + '-' + tab.key])
										.map(tab => (
										<button key={tab.key} onClick={() => setService(tab.key)} style={{
											borderRadius: '8px', border: service === tab.key ? '2px solid #635bff' : '1.5px solid #e3e8ee',
											background: service === tab.key ? '#f5f3ff' : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
											fontWeight: 600, color: service === tab.key ? '#635bff' : '#0a2540', transition: 'all 0.15s ease',
										}}>
											<i className={`fas ${tab.icon}`} style={{fontSize: '0.75rem'}}></i>
											{tab.label}
										</button>
									))}
								</div>
							</div>

							{/* Form area */}
							<div style={{padding: '16px'}}>
								<Tab.Content>
									{/* Followers */}
									<Tab.Pane eventKey="followers">
										<div>
											{/* Quick-select tiers. Most people want a round number at a
											    known price, and the slider made them hunt for it. Prices
											    come from the live catalog rate so a tier can never quote
											    something the order route won't charge. */}
											<div style={{marginTop: '16px'}}>
												<label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', display: 'block'}}>{L.booster.quickSelect}</label>
												<div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
													{followerTiers.map((qty) => {
														// catalogPrice, not rate x qty: followers price off the
														// anchor curve, so a flat per-1,000 quoted every tier
														// under 4,000 low — 1,000 read $6.25 against the $8.59
														// the order actually charged. Services with no curve
														// fall back to rate x qty inside the helper, so likes
														// and views are unchanged.
														const entry = prices[platform + '-followers'];
														const isActive = Number(followersValue) === qty && !customFollowers;
														const salePrice = entry ? catalogPrice(entry, qty) : null;
														return (
															<button
																key={qty}
																type="button"
																// Clicking the picked tier again clears it, so a selection
																// can be undone without choosing a different size.
																onClick={() => { setCustomFollowers(false); setFollowersValue(isActive ? 0 : qty); }}
																style={{
																	display: 'flex', alignItems: 'center', justifyContent: 'space-between',
																	width: '100%', padding: '13px 16px', borderRadius: '12px', cursor: 'pointer',
																	border: isActive ? '2px solid #635bff' : '1.5px solid #e3e8ee',
																	background: isActive ? 'rgba(99,91,255,0.04)' : '#fff',
																	boxShadow: isActive ? '0 4px 12px rgba(99,91,255,0.12)' : 'none',
																	transition: 'all 0.15s ease', textAlign: 'left',
																}}
															>
																<span style={{display: 'flex', alignItems: 'center', gap: 10, minWidth: 0}}>
																	<i className={`fa-brands fa-${platform} ${brandClass(platform)}`} style={{fontSize: '1.05rem', flexShrink: 0}}></i>
																	<span style={{fontWeight: 700, color: '#0a2540', fontSize: '0.92rem'}}>
																		{L.booster.tierLabelService(qty.toLocaleString(), L.booster.services.followers)}
																	</span>
																</span>
															<span style={{display: 'flex', alignItems: 'baseline', gap: 6, flexShrink: 0}}>
																{salePrice !== null && <span style={{fontWeight: 600, color: '#94a3b8', fontSize: '0.74rem', textDecoration: 'line-through'}}>${regularPackagePrice(salePrice).toFixed(2)}</span>}
																<span style={{fontWeight: 700, color: '#635bff', fontSize: '1.05rem'}}>{salePrice !== null ? `$${salePrice.toFixed(2)}` : '—'}</span>
															</span>
															</button>
														);
													})}

													{/* Custom amount, secondary to the tiers. */}
													<button
														type="button"
														onClick={() => setCustomFollowers((open) => !open)}
														style={{
															display: 'flex', alignItems: 'center', justifyContent: 'space-between',
															width: '100%', padding: '13px 16px', borderRadius: '12px', cursor: 'pointer',
															border: customFollowers ? '2px solid #635bff' : '1.5px solid #e3e8ee',
															background: customFollowers ? 'rgba(99,91,255,0.04)' : '#fff',
															transition: 'all 0.15s ease', textAlign: 'left',
														}}
													>
														<span style={{fontWeight: 600, color: '#0a2540', fontSize: '0.9rem'}}>{L.booster.customAmount}</span>
														<i className={`fa-solid fa-chevron-${customFollowers ? 'up' : 'down'}`} style={{fontSize: '0.7rem', color: '#8898aa'}}></i>
													</button>
												</div>

								{customFollowers && (
									<div style={{marginTop: '12px'}}>
										<label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px', display: 'block'}}>{L.booster.followersAmountMin(min)}</label>
										<Form.Control max={50000} type="number" value={followersValue || ''} placeholder="0" onChange={(val) => setFollowersValue(val.target.value)}
											style={{borderRadius: '8px', border: '1.5px solid #e3e8ee', fontSize: '0.88rem', padding: '10px 14px', marginBottom: '12px'}} />
										<ReactSlider className="custom-slider" thumbClassName="custom-thumb" trackClassName="custom-track" max={50000} value={Number(followersValue) || 0} onChange={(val) => setFollowersValue(val)} renderTrack={sliderTrack} />

										{platform === 'instagram' && prices[platform + '-followers']?.tiers?.anchors?.length > 0 && (
										<div style={{display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginTop: '16px'}}>
											{CUSTOM_FOLLOWER_TIERS.map((qty) => {
													const entry = prices[platform + '-followers'];
													const isActive = Number(followersValue) === qty;
													const salePrice = entry ? catalogPrice(entry, qty) : null;
												return (
													<button
														key={qty}
														type="button"
														onClick={() => setFollowersValue(isActive ? 0 : qty)}
												style={{
													display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
													width: '100%', minWidth: 0, minHeight: 82, padding: '10px 4px', borderRadius: '10px', cursor: 'pointer',
													border: isActive ? '2px solid #635bff' : '1.5px solid #e3e8ee',
													background: isActive ? 'rgba(99,91,255,0.06)' : '#fff',
													textAlign: 'center', transition: 'all 0.15s ease',
												}}
											>
												<span style={{fontWeight: 700, color: '#0a2540', fontSize: '0.74rem', lineHeight: 1.2}}>
													{L.booster.tierLabelService(qty.toLocaleString(), L.booster.services.followers)}
												</span>
														<span style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, lineHeight: 1}}>
															{salePrice !== null && <span style={{fontWeight: 600, color: '#94a3b8', fontSize: '0.66rem', textDecoration: 'line-through'}}>${regularPackagePrice(salePrice).toFixed(2)}</span>}
															<span style={{fontWeight: 800, color: '#635bff', fontSize: '0.9rem'}}>{salePrice !== null ? `$${salePrice.toFixed(2)}` : '—'}</span>
														</span>
													</button>
												);
											})}
										</div>
										)}
									</div>
								)}
											</div>
										</div>
									</Tab.Pane>

									{/* Likes */}
									<Tab.Pane eventKey="likes">
										<div>
																						{/* Round amounts first, custom below — most people want a
											    number at a known price rather than a slider. */}
											<div style={{marginTop: '16px'}}>
												<label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', display: 'block'}}>{L.booster.quickSelect}</label>
												<div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
													{(SERVICE_TIERS.likes || []).map((qty) => {
														const entry = prices[platform + '-likes'];
														const isActive = Number(likesValue) === qty && !customLikes && !selectedPack;
														return (
															<button
																key={qty}
																type="button"
																onClick={() => { setSelectedPack(null); setCustomLikes(false); setLikesValue(isActive ? 0 : qty); }}
																style={{
																	display: 'flex', alignItems: 'center', justifyContent: 'space-between',
																	width: '100%', padding: '13px 16px', borderRadius: '12px', cursor: 'pointer',
																	border: isActive ? '2px solid #635bff' : '1.5px solid #e3e8ee',
																	background: isActive ? 'rgba(99,91,255,0.04)' : '#fff',
																	transition: 'all 0.15s ease', textAlign: 'left',
																}}
															>
																<span style={{display: 'flex', alignItems: 'center', gap: 10}}>
																	<i className={`fa-brands fa-${platform} ${brandClass(platform)}`} style={{fontSize: '1.05rem', flexShrink: 0}}></i>
																	<span style={{fontWeight: 700, color: '#0a2540', fontSize: '0.92rem'}}>
																		{L.booster.tierLabelService(qty.toLocaleString(), L.booster.services.likes)}
																	</span>
																</span>
																<span style={{fontWeight: 700, color: '#635bff', fontSize: '1.05rem'}}>
																	{entry ? `$${catalogPrice(entry, qty).toFixed(2)}` : '—'}
																</span>
															</button>
														);
													})}
												</div>
											</div>
											{/* Custom amount, secondary to the tiers above. */}
											<div style={{marginTop: '10px'}}>
												<button
													type="button"
													onClick={() => { setSelectedPack(null); setCustomLikes((v) => !v); }}
													style={{
														display: 'flex', alignItems: 'center', justifyContent: 'space-between',
														width: '100%', padding: '13px 16px', borderRadius: '12px', cursor: 'pointer',
														border: customLikes ? '2px solid #635bff' : '1.5px solid #e3e8ee',
														background: customLikes ? 'rgba(99,91,255,0.04)' : '#fff', textAlign: 'left',
													}}
												>
													<span style={{fontWeight: 600, color: '#0a2540', fontSize: '0.9rem'}}>{L.booster.customAmount}</span>
													<i className={`fa-solid fa-chevron-${customLikes ? 'up' : 'down'}`} style={{fontSize: '0.7rem', color: '#8898aa'}}></i>
												</button>
											</div>
											{customLikes && (
							<div style={{marginTop: '16px'}}>
												<label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px', display: 'block'}}>{L.booster.likesAmount}</label>
												<Form.Control max={5000} type="number" value={likesValue} onChange={(val) => setLikesValue(val.target.value)}
													style={{borderRadius: '8px', border: '1.5px solid #e3e8ee', fontSize: '0.88rem', padding: '10px 14px', marginBottom: '12px'}} />
												<ReactSlider className="custom-slider" thumbClassName="custom-thumb" trackClassName="custom-track" max={5000} value={likesValue} onChange={(val) => setLikesValue(val)} renderTrack={sliderTrack} />
											</div>
											)}
											{/* Engagement bundles. Likes and views together, spread over
											    recent posts — the pairing is what reads as real engagement,
											    and buying them separately is the fiddly version of this.
											    Instagram and TikTok only: the spread depends on the post
											    lookup, which the other platforms have no equivalent for. */}
											{BUNDLE_PLATFORMS.includes(platform) && (
											<div style={{marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9'}}>
												<label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', display: 'block'}}>{L.booster.engagementBundles}</label>
												<div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
													{ENGAGEMENT_PACKS.map((pk) => (
														<button
															key={pk.id}
															type="button"
															// Clicking the selected pack again clears it, so there is a
															// way out of a bundle without picking a different one.
															onClick={() => { setSelectedPack((cur) => (cur?.id === pk.id ? null : pk)); setLikesValue(0); setViewsValue(0); }}
															style={{
																display: 'flex', alignItems: 'center', justifyContent: 'space-between',
																width: '100%', padding: '13px 16px', borderRadius: '12px',
																cursor: 'pointer',
																border: selectedPack?.id === pk.id ? '2px solid #635bff' : '1.5px solid #e3e8ee',
																background: selectedPack?.id === pk.id ? 'rgba(99,91,255,0.04)' : '#fff',
																transition: 'all 0.15s ease', textAlign: 'left',
															}}
														>
															<span style={{display: 'flex', alignItems: 'center', gap: 10, minWidth: 0}}>
																<span style={{fontSize: '1.05rem', flexShrink: 0}}>{pk.icon}</span>
																<span style={{minWidth: 0}}>
																	<span style={{display: 'block', fontWeight: 700, color: '#0a2540', fontSize: '0.9rem'}}>
																		{L.booster.bundleLabel(pk.likes.toLocaleString(), pk.views.toLocaleString())}
																	</span>
																	<span style={{display: 'block', color: '#8898aa', fontSize: '0.75rem'}}>
																		{L.booster.bundleSub(platform.charAt(0).toUpperCase() + platform.slice(1))}
																	</span>
																</span>
															</span>
															<span style={{fontWeight: 700, color: '#635bff', fontSize: '1.05rem', flexShrink: 0}}>
																${pk.price.toFixed(2)}
															</span>
														</button>
													))}
												</div>
											</div>
											)}
										</div>
									</Tab.Pane>

									{/* Views */}
									<Tab.Pane eventKey="views">
										<div>
																						{/* Round amounts first, custom below — most people want a
											    number at a known price rather than a slider. */}
											<div style={{marginTop: '16px'}}>
												<label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', display: 'block'}}>{L.booster.quickSelect}</label>
												<div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
													{(SERVICE_TIERS.views || []).map((qty) => {
														const entry = prices[platform + '-views'];
														const isActive = Number(viewsValue) === qty && !customViews && !selectedPack;
														return (
															<button
																key={qty}
																type="button"
																onClick={() => { setSelectedPack(null); setCustomViews(false); setViewsValue(isActive ? 0 : qty); }}
																style={{
																	display: 'flex', alignItems: 'center', justifyContent: 'space-between',
																	width: '100%', padding: '13px 16px', borderRadius: '12px', cursor: 'pointer',
																	border: isActive ? '2px solid #635bff' : '1.5px solid #e3e8ee',
																	background: isActive ? 'rgba(99,91,255,0.04)' : '#fff',
																	transition: 'all 0.15s ease', textAlign: 'left',
																}}
															>
																<span style={{display: 'flex', alignItems: 'center', gap: 10}}>
																	<i className={`fa-brands fa-${platform} ${brandClass(platform)}`} style={{fontSize: '1.05rem', flexShrink: 0}}></i>
																	<span style={{fontWeight: 700, color: '#0a2540', fontSize: '0.92rem'}}>
																		{L.booster.tierLabelService(qty.toLocaleString(), L.booster.services.views)}
																	</span>
																</span>
																<span style={{fontWeight: 700, color: '#635bff', fontSize: '1.05rem'}}>
																	{entry ? `$${catalogPrice(entry, qty).toFixed(2)}` : '—'}
																</span>
															</button>
														);
													})}
												</div>
											</div>
											{/* Custom amount, secondary to the tiers above. */}
											<div style={{marginTop: '10px'}}>
												<button
													type="button"
													onClick={() => { setSelectedPack(null); setCustomViews((v) => !v); }}
													style={{
														display: 'flex', alignItems: 'center', justifyContent: 'space-between',
														width: '100%', padding: '13px 16px', borderRadius: '12px', cursor: 'pointer',
														border: customViews ? '2px solid #635bff' : '1.5px solid #e3e8ee',
														background: customViews ? 'rgba(99,91,255,0.04)' : '#fff', textAlign: 'left',
													}}
												>
													<span style={{fontWeight: 600, color: '#0a2540', fontSize: '0.9rem'}}>{L.booster.customAmount}</span>
													<i className={`fa-solid fa-chevron-${customViews ? 'up' : 'down'}`} style={{fontSize: '0.7rem', color: '#8898aa'}}></i>
												</button>
											</div>
											{customViews && (
							<div style={{marginTop: '16px'}}>
												<label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px', display: 'block'}}>{L.booster.viewsAmount}</label>
												<Form.Control max={50000} type="number" value={viewsValue} onChange={(val) => setViewsValue(val.target.value)}
													style={{borderRadius: '8px', border: '1.5px solid #e3e8ee', fontSize: '0.88rem', padding: '10px 14px', marginBottom: '12px'}} />
												<ReactSlider className="custom-slider" thumbClassName="custom-thumb" trackClassName="custom-track" max={50000} value={viewsValue} onChange={(val) => setViewsValue(val)} renderTrack={sliderTrack} />
											</div>
											)}
											{/* Engagement bundles. Likes and views together, spread over
											    recent posts — the pairing is what reads as real engagement,
											    and buying them separately is the fiddly version of this.
											    Instagram and TikTok only: the spread depends on the post
											    lookup, which the other platforms have no equivalent for. */}
											{BUNDLE_PLATFORMS.includes(platform) && (
											<div style={{marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9'}}>
												<label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', display: 'block'}}>{L.booster.engagementBundles}</label>
												<div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
													{ENGAGEMENT_PACKS.map((pk) => (
														<button
															key={pk.id}
															type="button"
															// Clicking the selected pack again clears it, so there is a
															// way out of a bundle without picking a different one.
															onClick={() => { setSelectedPack((cur) => (cur?.id === pk.id ? null : pk)); setLikesValue(0); setViewsValue(0); }}
															style={{
																display: 'flex', alignItems: 'center', justifyContent: 'space-between',
																width: '100%', padding: '13px 16px', borderRadius: '12px',
																cursor: 'pointer',
																border: selectedPack?.id === pk.id ? '2px solid #635bff' : '1.5px solid #e3e8ee',
																background: selectedPack?.id === pk.id ? 'rgba(99,91,255,0.04)' : '#fff',
																transition: 'all 0.15s ease', textAlign: 'left',
															}}
														>
															<span style={{display: 'flex', alignItems: 'center', gap: 10, minWidth: 0}}>
																<span style={{fontSize: '1.05rem', flexShrink: 0}}>{pk.icon}</span>
																<span style={{minWidth: 0}}>
																	<span style={{display: 'block', fontWeight: 700, color: '#0a2540', fontSize: '0.9rem'}}>
																		{L.booster.bundleLabel(pk.likes.toLocaleString(), pk.views.toLocaleString())}
																	</span>
																	<span style={{display: 'block', color: '#8898aa', fontSize: '0.75rem'}}>
																		{L.booster.bundleSub(platform.charAt(0).toUpperCase() + platform.slice(1))}
																	</span>
																</span>
															</span>
															<span style={{fontWeight: 700, color: '#635bff', fontSize: '1.05rem', flexShrink: 0}}>
																${pk.price.toFixed(2)}
															</span>
														</button>
													))}
												</div>
											</div>
											)}
										</div>
									</Tab.Pane>

									{/* Comments */}
									<Tab.Pane eventKey="comments">
										<div>
											<div style={{marginTop: '16px'}}>
												<label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px', display: 'block'}}>{L.booster.customComments}</label>
												<Form.Control as="textarea" rows={4} placeholder={L.booster.commentsPlaceholder} style={{resize: 'vertical', borderRadius: '8px', border: '1.5px solid #e3e8ee', fontSize: '0.88rem', padding: '10px 14px'}}
													onChange={(e) => { const lines = e.target.value.split('\n').filter(line => line.trim() !== '').length; setCommentsValue(lines); setComments(e.target.value.replace(/\n/g, '|')); }} />
											</div>
										</div>
									</Tab.Pane>
								</Tab.Content>

								{/* Post selector grid */}
								{posts.length > 0 && (
									<div ref={postsRef} style={{marginTop: '16px', scrollMarginTop: '80px'}}>
										<label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', display: 'block'}}>{L.booster.selectAPost}</label>
										<div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px'}}>
											{posts.map((post) => {
												const isPicked = selectedPosts.includes(post.post_url);
												const atCap = !isPicked && selectedPosts.length >= maxSelectablePosts();
												return (
												<div key={post.post_id} onClick={() => { if (!atCap) togglePost(post.post_url); }} style={{
													position: 'relative',
													borderRadius: '10px', overflow: 'hidden', transition: 'all 0.15s ease',
													cursor: atCap ? 'not-allowed' : 'pointer',
													opacity: atCap ? 0.45 : 1,
													border: isPicked ? '2px solid #635bff' : '1.5px solid #e3e8ee',
													boxShadow: isPicked ? '0 0 0 3px rgba(99,91,255,0.1)' : 'none',
												}}>
													{/* What this specific post gets. Shown on the tile so the
													    split is visible while choosing, not after paying. */}
													{isPicked && (
														<div style={{
															position: 'absolute', inset: 0, background: 'rgba(99,91,255,0.82)',
															display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
															gap: 2, zIndex: 2, pointerEvents: 'none',
														}}>
															{activeSplit().map((sp) => (
																<span key={sp.kind} style={{color: '#fff', fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.2}}>
																	+{sp.per.toLocaleString()} {L.booster.services[sp.kind] || sp.kind}
																</span>
															))}
														</div>
													)}
													<img src={`https://rizzup.net/proxy/${encodeURIComponent(post.post_image)}`} alt="Post" style={{width: '100%', height: '100px', objectFit: 'cover'}} onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.png'; }} />
													<div style={{padding: '8px 10px'}}>
														<p style={{fontSize: '0.72rem', color: '#68778d', margin: '0 0 4px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}>{post.post_text}</p>
														<div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#94a3b8'}}>
															<span><i className="fas fa-heart me-1"></i>{post.post_likes}</span>
															<span><i className="fas fa-comment me-1"></i>{post.post_comments}</span>
															<span><i className="fas fa-eye me-1"></i>{post.post_views}</span>
														</div>
													</div>
												</div>
												);
											})}
										</div>

										{/* Selection cap, explained where it bites rather than at submit. */}
										{selectedPosts.length > 0 && (
											<p style={{fontSize: '0.72rem', color: '#68778d', margin: '8px 0 0'}}>
												{L.booster.postsChosen(selectedPosts.length, maxSelectablePosts())}
											</p>
										)}

										{/* Pagination */}
										<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '12px'}}>
											{currentPage > 0 && (
												<button onClick={() => { setCurrentPage(currentPage - 1); getPosts(currentPage - 1); }} style={{padding: '8px 14px', borderRadius: '8px', border: '1.5px solid #e3e8ee', background: '#fff', cursor: 'pointer', color: '#0a2540', fontWeight: 600, fontSize: '0.82rem'}}>
													<i className="fas fa-arrow-left"></i>
												</button>
											)}
											<span style={{fontSize: '0.82rem', color: '#68778d', fontWeight: 500}}>
												{loadingPage ? <i className="fas fa-arrows-rotate fa-spin"></i> : L.booster.pageN(currentPage + 1)}
											</span>
											<button onClick={() => { setCurrentPage(currentPage + 1); getPosts(currentPage + 1); }} disabled={!cursors[currentPage] || loadingPage}
												style={{padding: '8px 14px', borderRadius: '8px', border: '1.5px solid #e3e8ee', background: '#fff', cursor: !cursors[currentPage] || loadingPage ? 'not-allowed' : 'pointer', color: '#0a2540', fontWeight: 600, fontSize: '0.82rem', opacity: !cursors[currentPage] || loadingPage ? 0.4 : 1}}>
												<i className="fas fa-arrow-right"></i>
											</button>
										</div>
									</div>
								)}

							</div>
						</div>


						{/* Which account this delivers to. It applies to every service, so
						    it gets its own card rather than being repeated inside each
						    tab — and it defaults to the linked profile when the platform
						    matches, so the common case needs no typing. */}
						{/* Shows where the order goes. The handle itself is set in the
						    profile popup, so with none set there is nothing to show. */}
						{postLink && (
						<div ref={accountRef} style={{background: '#fff', borderRadius: '12px', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)', padding: '16px', scrollMarginTop: '80px'}}>
								<>
									<div style={{display: 'flex', alignItems: 'center', gap: 11}}>
										{/* The linked profile's own photo, but only when the handle in
										    the field is still that profile — showing someone's avatar
										    beside a different account would misrepresent the order. */}
										{deliveringToLinkedProfile() && linkedProfile()?.photo && (
											<img
												src={linkedProfile().photo}
												alt=""
												style={{width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid #e3e8ee'}}
												onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
											/>
										)}
										<div style={{fontWeight: 700, color: '#0a2540', fontSize: '0.95rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
											{L.booster.deliverTo(postLink.replace(/^@/, ''))}
										</div>
									</div>
									<div style={{display: 'flex', alignItems: 'center', gap: 14, marginTop: 8, flexWrap: 'wrap'}}>
										<button
											type="button"
											onClick={() => setShowProfileModal(true)}
											style={{background: 'none', border: 'none', padding: 0, color: '#635bff', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer'}}
										>
											{L.booster.changeAccount}
										</button>
										{/* The posts belong to this account, so the way to load them
										    belongs beside it rather than repeated in each tab. */}
										{showSearchBtn(service) && (
											<button
												type="button"
												onClick={() => getPosts(0)}
												style={{background: 'none', border: 'none', padding: 0, color: '#635bff', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer'}}
											>
												{loadingPosts
													? <><i className="fas fa-arrows-rotate fa-spin me-1"></i>{L.booster.loadingPosts}</>
													: <><i className="fas fa-images me-1"></i>{L.booster.loadPosts}</>}
											</button>
										)}
									</div>
								</>
						</div>
						)}

						{/* Order summary */}
						{hasValue && (
							<div style={{background: '#fff', borderRadius: '12px', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)', overflow: 'hidden'}}>
								<div style={{padding: '20px'}}>
									<h5 style={{color: '#0a2540', fontWeight: 700, fontSize: '0.9rem', marginBottom: '16px'}}>{L.booster.orderSummary}</h5>

									<div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
										{selectedPack ? (
											<>
												<div style={{display: 'flex', justifyContent: 'space-between'}}>
													<span style={{color: '#68778d', fontSize: '0.82rem'}}>{L.booster.services.likes}</span>
													<span style={{color: '#0a2540', fontWeight: 600, fontSize: '0.82rem'}}>{selectedPack.likes.toLocaleString()}</span>
												</div>
												<div style={{display: 'flex', justifyContent: 'space-between'}}>
													<span style={{color: '#68778d', fontSize: '0.82rem'}}>{L.booster.services.views}</span>
													<span style={{color: '#0a2540', fontWeight: 600, fontSize: '0.82rem'}}>{selectedPack.views.toLocaleString()}</span>
												</div>
												<div style={{display: 'flex', justifyContent: 'space-between'}}>
													<span style={{color: '#68778d', fontSize: '0.82rem'}}>{L.booster.bundleSpread}</span>
													<span style={{color: '#0a2540', fontWeight: 600, fontSize: '0.82rem'}}>{L.booster.recentPosts}</span>
												</div>
											</>
										) : (
										<>
										<div style={{display: 'flex', justifyContent: 'space-between'}}>
											<span style={{color: '#68778d', fontSize: '0.82rem'}}>{L.booster.pricePer(service.replace("s", ""))}</span>
											<span style={{color: '#0a2540', fontWeight: 600, fontSize: '0.82rem'}}>${isNaN(normalPrice / quantity) ? 0 : (normalPrice / quantity).toFixed(3)}</span>
										</div>
										<div style={{display: 'flex', justifyContent: 'space-between'}}>
											<span style={{color: '#68778d', fontSize: '0.82rem'}}>{L.booster.quantity}</span>
											<span style={{color: '#0a2540', fontWeight: 600, fontSize: '0.82rem'}}>{quantity}</span>
										</div>
										<div style={{display: 'flex', justifyContent: 'space-between'}}>
											<span style={{color: '#68778d', fontSize: '0.82rem'}}>{L.booster.subtotal}</span>
											<span style={{color: '#0a2540', fontWeight: 600, fontSize: '0.82rem'}}>${normalPrice}</span>
										</div>
										</>
										)}
									</div>

									<div style={{borderTop: '1px solid #e3e8ee', margin: '14px 0', paddingTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
										<span style={{color: '#0a2540', fontSize: '0.88rem', fontWeight: 600}}>{L.booster.total}</span>
										<span style={{color: '#0a2540', fontSize: '1.3rem', fontWeight: 700}}>${selectedPack ? selectedPack.price.toFixed(2) : (Number(price) || 0).toFixed(2)}</span>
									</div>

									{/* CTA button */}
									{/* A missing post is a step to take, not a reason to grey the
									    button out — it leads there instead of describing it. Only a
									    disabled service leaves nothing to press. */}
									<button
										onClick={ctaAction}
										disabled={selectedPack ? packLoading : (isDisabled && !needsPost)}
										style={{
											width: '100%', padding: '12px', borderRadius: '10px', border: 'none', fontWeight: 700, fontSize: '0.9rem',
											cursor: (isDisabled && !needsPost) ? 'not-allowed' : 'pointer', transition: 'all 0.15s ease',
											background: (isDisabled && !needsPost) ? '#e2e8f0' : 'linear-gradient(135deg, #635bff 0%, #8b5cf6 100%)',
											color: (isDisabled && !needsPost) ? '#94a3b8' : '#fff',
											boxShadow: (isDisabled && !needsPost) ? 'none' : '0 2px 8px rgba(99,91,255,0.3)',
											opacity: (isDisabled && !needsPost) ? 0.7 : 1,
										}}
									>
										{pendingSend ? (
											<><i className="fas fa-spinner fa-spin" style={{marginRight: '6px'}}></i> {L.booster.processing}</>
										) : prices[platform + "-" + service]?.enabled === 0
											? L.booster.serviceUnavailable
											: ctaPendingText ? <><i className={`fas ${needsProfile ? 'fa-user' : 'fa-images'}`} style={{marginRight: '6px'}}></i>{ctaPendingText}</>
											: L.booster.buyNowAmount((selectedPack ? selectedPack.price : (Number(price) || 0)).toFixed(2))
										}
									</button>

									{/* Trust badges */}
									<div style={{display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '14px', paddingTop: '10px', borderTop: '1px solid #f1f5f9'}}>
										<span style={{fontSize: '0.72rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px'}}>
											<i className="fas fa-lock" style={{fontSize: '0.6rem', color: '#635bff'}}></i> {L.booster.noPassword}
										</span>
										<OverlayTrigger placement="top" overlay={<Tooltip>{L.booster.avgDeliveryTime}</Tooltip>}>
											<span style={{fontSize: '0.72rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help'}}>
												<i className="fas fa-clock" style={{fontSize: '0.6rem', color: '#635bff'}}></i> {prices[platform + "-" + service]?.averageTime}
											</span>
										</OverlayTrigger>
									</div>
								</div>
							</div>
						)}

						{/* Order history */}
						<div ref={ordersRef} style={{background: '#fff', borderRadius: '12px', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)', overflow: 'hidden', marginTop: '8px', scrollMarginTop: '80px'}}>
							<div style={{padding: '16px 20px', borderBottom: '1px solid #e3e8ee'}}>
								<h5 style={{color: '#0a2540', fontWeight: 700, fontSize: '0.95rem', margin: 0}}>{L.booster.orderHistory}</h5>
							</div>

							{orders.length === 0 ? (
								<div style={{padding: '32px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem'}}>
									<i className="fas fa-inbox" style={{fontSize: '1.5rem', marginBottom: '8px', display: 'block'}}></i>
									{L.booster.noOrders}
								</div>
							) : (
								<>
									{/* Desktop table. The card around it is overflow:hidden, so
									    without a scroller of its own the last column is clipped
									    rather than reachable — which is what happened once the
									    Send button widened Status. Nine columns don't always fit
									    a laptop width. */}
									<div className="d-none d-md-block" style={{overflowX: 'auto'}}>
										<Table className="align-middle mb-0" style={{borderCollapse: 'separate', borderSpacing: 0}}>
											<thead>
												<tr>
													<th style={thStyle}>{L.booster.thService}</th>
													<th style={thStyle}>{L.booster.thLink}</th>
													<th style={thStyle}>{L.booster.thCharge}</th>
													<th style={thStyle}>{L.booster.thStart}</th>
													<th style={thStyle}>{L.booster.thAmount}</th>
													<th style={thStyle}>{L.booster.thRemains}</th>
													<th style={thStyle}>{L.booster.thStatus}</th>
													<th style={thStyle}>{L.booster.thDate}</th>
													<th style={thStyle}></th>
												</tr>
											</thead>
											<tbody>
							{orders.map((order, index) => {
									return (
														<React.Fragment key={index}>
														<tr
															onClick={() => hasDetails(order) && setDetailsOrder(order)}
															style={{transition: 'background 0.1s', cursor: hasDetails(order) ? 'pointer' : 'default'}}
															onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
															onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
														>
															<td style={tdStyle}>
																<i className={`fa-brands fa-${order.boost_type.split('-')[0]} me-2 ${brandClass(order.boost_type.split('-')[0])}`}></i>
																{serviceName(order)}
															</td>
															<td style={tdStyle}>
																{/* The icon alone gave no clue which account or post an
																    order was for; the title surfaces it on hover. */}
																<a href={order.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title={orderLinkLabel(order.link)} style={{color: '#635bff', textDecoration: 'none'}}>
																	<i className="fas fa-arrow-up-right-from-square" style={{fontSize: '0.7rem'}}></i>
																</a>
															</td>
															<td style={{...tdStyle, fontWeight: 600}}>{orderPrice(order)}</td>
															<td style={tdMuted}>{num(order.start_count)}</td>
															<td style={tdMuted}>{deliveryQuantity(order.quantity)}</td>
															<td style={tdMuted}>{num(order.remains)}</td>
															<td style={tdStyle}>
																<OrderStatus order={order} />
															</td>
															<td style={tdMuted}>{new Date(order.created_at).toLocaleDateString()}</td>
															<td style={{...tdMuted, textAlign: 'right'}}>
																{hasDetails(order) && (
																	<button
																		type="button"
																		onClick={() => setDetailsOrder(order)}
																		style={{background: 'none', border: 'none', color: '#635bff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, padding: 0}}
																	>
																		{L.booster.viewDetails}
																	</button>
																)}
															</td>
														</tr>
														</React.Fragment>
													);
												})}
											</tbody>
										</Table>
									</div>

									{/* Mobile cards */}
									<div className="d-md-none" style={{padding: '12px'}}>
										{orders.map((order, index) => {
											return (
												<div
													key={index}
													onClick={() => hasDetails(order) && setDetailsOrder(order)}
													style={{borderRadius: '10px', border: '1px solid #f1f5f9', padding: '14px', marginBottom: '8px', background: '#fff', cursor: hasDetails(order) ? 'pointer' : 'default'}}
												>
													<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
														<span style={{fontWeight: 600, fontSize: '0.82rem', color: '#0a2540'}}>
															<i className={`fa-brands fa-${order.boost_type.split('-')[0]} me-1 ${brandClass(order.boost_type.split('-')[0])}`}></i>
															{serviceName(order)}
														</span>
														{/* No status chip on a resendable order — the full-width Send
														    button below is the whole point, and the stored status is
														    the one nobody can act on. */}
														<OrderStatus order={order} mobile />
													</div>
													<div style={{display: 'flex', gap: '16px', fontSize: '0.78rem', color: '#68778d'}}>
														<span><strong style={{color: '#0a2540'}}>{orderPrice(order)}</strong></span>
														<span>{L.booster.qty(deliveryQuantity(order.quantity))}</span>
														<span>{new Date(order.created_at).toLocaleDateString()}</span>
													</div>
													{/* needsUsername excluded: OrderStatus above already renders the
													    username input for those rows, and a plain Send beneath it just
													    reaches a backend that refuses with username_required. */}
													{isResendable(order) && !needsUsername(order) && (
														<div style={{marginTop: 12}}>
															<SendButton order={order} block />
														</div>
													)}
													{/* The table's Details column is hidden at this width, so the card
													    needs its own way in. */}
													{hasDetails(order) && (
														<div style={{marginTop: 8, color: '#635bff', fontSize: '0.75rem', fontWeight: 600}}>
															{L.booster.viewDetails} <i className="fa-solid fa-chevron-right" style={{fontSize: '0.6rem'}}></i>
														</div>
													)}
												</div>
											);
										})}
									</div>
								</>
							)}
						</div>

					</div>
				</Tab.Container>
			</Container>

			{/* Pay step. Reached only when the balance won't cover the boost, so
			    the question is how to fund it: pay what this one costs, or add
			    funds and take the bonus. Either way the boost is placed as soon
			    as the money lands. */}
			<Modal show={!!payStep} onHide={() => { if (!addonLoading) setPayStep(null); }} centered size="sm">
				<Modal.Header closeButton>
					<Modal.Title style={{fontSize: '1rem', fontWeight: 700, color: '#0a2540'}}>
						{payStep === 'topup' ? L.booster.addFundsTitle : L.booster.buyNow}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{/* One BalanceTopUp at a time: CollectJS binds the wallet buttons
					    to fixed element ids, so a second copy would kill the first. */}
					{(payStep === 'exact' || payStep === 'topup') && (
						<BalanceTopUp
							inline
							profile={profile}
							onHide={() => setPayStep(null)}
							fixedAmount={payStep === 'exact' ? payAmount : null}
							directPack={payStep === 'exact'}
							onSuccess={async (added) => {
								setPayStep(null);
								profile.balance = (Number(profile.balance) || 0) + Number(added || 0);
								setFunctionError("");
								// The add-ons they ticked are part of the total they just funded
								// — clearing them here made the reopened confirm show a lower
								// figure with their extras silently unpicked.
								setShowAddons(true);
							}}
						/>
					)}
				</Modal.Body>
			</Modal>

			{/* Confirm step. Nothing has been charged at this point — closing it
			    cancels outright, and the add-ons change the total, so the final
			    figure is settled here before any money moves. */}
			{/* Not size="sm": that caps the dialog at 300px, which was fine when
			    this was a short list of checkboxes and is not now it carries an
			    icon, a title, a subtitle and a price on one row, plus two
			    full-width buttons under a summary. The default 500px is still
			    narrower than the viewport on any phone, so nothing regresses
			    there. */}
			<Modal show={showAddons} onHide={() => { if (!addonLoading) setShowAddons(false); }} centered>
				<Modal.Header closeButton>
					<Modal.Title style={{fontSize: '1rem', fontWeight: 700, color: '#0a2540'}}>{L.booster.confirmTitle}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{/* What they are actually buying, restated so the popup is a
					    confirmation and not just an upsell. */}
					<div style={{padding: '11px 13px', marginBottom: 12, borderRadius: 10, background: '#f6f9fc'}}>
						<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
							<span style={{fontWeight: 600, color: '#0a2540', fontSize: '0.85rem'}}>
								{selectedPack
									? L.booster.bundleLabel(selectedPack.likes.toLocaleString(), selectedPack.views.toLocaleString())
									: `${Number(quantity || 0).toLocaleString()} ${L.booster.services[service] || service}`}
							</span>
							<span style={{fontWeight: 700, color: '#0a2540', fontSize: '0.9rem'}}>
								${selectedPack ? selectedPack.price.toFixed(2) : (Number(price) || 0).toFixed(2)}
							</span>
						</div>
						{/* Everything added on top, itemised under the boost it applies to. The
						    total at the foot is the only figure that charges, and a customer who
						    cannot see what built it reads the difference as a surprise. */}
						{!selectedPack && upcharges().map((u) => (
							<div key={u.label} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6}}>
								<span style={{color: '#68778d', fontSize: '0.78rem'}}>{u.label}</span>
								<span style={{color: '#635bff', fontSize: '0.78rem', fontWeight: 600}}>+${u.amount.toFixed(2)}</span>
							</div>
						))}
					</div>
					{/* Grade, chosen here rather than on the card behind it: it changes
					    the price, and every figure that moves belongs on the screen the
					    customer confirms. Seeded from what they bought last time. */}
					{!selectedPack && service === 'followers' && (
						<div style={{marginBottom: 14}}>
							<div style={{fontSize: '0.72rem', fontWeight: 700, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6}}>{L.booster.optQuality}</div>
							<div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6}}>
								{QUALITY_TIERS.map((t) => (
									<button
										key={t.key}
										type="button"
										onClick={() => setQuality(t.key)}
										style={{
											padding: '8px 4px', borderRadius: 8, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
											border: quality === t.key ? '2px solid #635bff' : '1.5px solid #e3e8ee',
											background: quality === t.key ? 'rgba(99,91,255,0.05)' : '#fff',
											color: quality === t.key ? '#635bff' : '#0a2540',
										}}
									>
										{L.fiftyoff.quality.tiers[t.key].name.replace(/ Followers$/, '')}
										<span style={{display: 'block', fontWeight: 600, color: '#8898aa', fontSize: '0.68rem'}}>
											${qualityPrice(Number(price) || 0, t.key).toFixed(2)}
										</span>
									</button>
								))}
							</div>
						</div>
					)}
					<p style={{color: '#68778d', fontSize: '0.85rem', marginBottom: 14}}>{L.booster.addonSub}</p>
					{BOOST_ADDONS.map((a) => {
						const checked = chosenAddons.includes(a.key);
						return (
							<div
								key={a.key}
								onClick={() => {
									const setter = { fast: setOptFast, female: setOptFemale, usa: setOptUsa }[a.key];
									if (setter) setter(!checked);
									setChosenAddons((prev) => checked ? prev.filter((k) => k !== a.key) : [...prev, a.key]);
								}}
								style={{
									display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', marginBottom: 8,
									borderRadius: 10, cursor: 'pointer',
									border: checked ? '2px solid #635bff' : '1.5px solid #e3e8ee',
									background: checked ? 'rgba(99,91,255,0.04)' : '#fff',
								}}
							>
								<input type="checkbox" checked={checked} readOnly style={{width: 15, height: 15, accentColor: '#635bff', flexShrink: 0}} />
								{/* Sized and centred in a fixed box so the three titles line up
								    whatever width each glyph renders at — the flag is wider than
								    the others on most platforms. */}
								<span
									aria-hidden="true"
									style={{
										flexShrink: 0, width: 26, height: 26, borderRadius: 8,
										display: 'flex', alignItems: 'center', justifyContent: 'center',
										fontSize: '0.95rem', lineHeight: 1,
										background: checked ? 'rgba(99,91,255,0.10)' : '#f6f9fc',
									}}
								>
									{a.icon}
								</span>
								<span style={{flex: 1, minWidth: 0}}>
									<span style={{display: 'block', fontWeight: 600, color: '#0a2540', fontSize: '0.85rem'}}>{L.booster.addons[a.key].title}</span>
									<span style={{display: 'block', color: '#8898aa', fontSize: '0.75rem'}}>{L.booster.addons[a.key].sub}</span>
								</span>
								<span style={{fontWeight: 700, color: '#635bff', fontSize: '0.9rem', flexShrink: 0}}>+${addonPriceFor(selectedPack ? selectedPack.price : Number(price) || 0).toFixed(2)}</span>
							</div>
						);
					})}

				</Modal.Body>
				<Modal.Footer style={{borderTop: '1px solid #e3e8ee', display: 'block'}}>
					{/* Balance is the only way a boost is paid for, so name it rather
					    than leaving the customer to wonder what Pay will charge. A
					    balance too small to cover this never reaches here — Start boost
					    sends them to Add Balance first. */}
					<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
						<span style={{color: '#68778d', fontSize: '0.8rem'}}>
							<i className="fas fa-wallet" style={{marginRight: 6, color: '#635bff'}}></i>{L.booster.payingWithBalance}
						</span>
						<span style={{color: '#0a2540', fontSize: '0.8rem', fontWeight: 600}}>${(Number(profile?.balance) || 0).toFixed(2)}</span>
					</div>
					<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
						<span style={{color: '#0a2540', fontWeight: 700, fontSize: '0.95rem'}}>
							{L.booster.total} ${confirmTotal()}
						</span>
					</div>
					{/* Two ways to pay, always both offered. Buy now spends the balance
					    when it stretches and charges exactly the shortfall when it
					    doesn't; add funds takes the bonus and leaves the rest on the
					    account. The bonus quoted is the real one for the tier that
					    would cover this. */}
					<Button onClick={() => {
						const owed = Number(confirmTotal()) - (Number(profile?.balance) || 0);
						if (owed <= 0) { confirmPurchase(); return; }
						setPayAmount(Math.round(owed * 100) / 100);
						setShowAddons(false);
						setPayStep('exact');
					}} disabled={addonLoading} className="w-100"
						style={{background: '#635bff', border: 'none', fontSize: '0.85rem', padding: '10px'}}>
						{addonLoading ? L.booster.processing : L.booster.payNow(confirmTotal())}
					</Button>
					<button type="button" onClick={() => { setShowAddons(false); setPayStep('topup'); }}
						disabled={addonLoading}
						style={{
							width: '100%', marginTop: 8, padding: '10px', borderRadius: 8, cursor: 'pointer',
							border: '1.5px solid #e3e8ee', background: '#fff', color: '#0a2540',
							fontSize: '0.82rem', fontWeight: 700,
						}}>
						{L.booster.addFundsBonus((closestTier(confirmTotal()).credit - closestTier(confirmTotal()).pay).toFixed(0))}
					</button>
				</Modal.Footer>
			</Modal>

			{/* Order details. Speed and targeting were bought with the package
			    rather than as separate orders, so they belong here rather than as
			    extra rows in the table. */}
			<Modal show={!!detailsOrder} onHide={() => setDetailsOrder(null)} centered size="sm">
				<Modal.Header closeButton>
					<Modal.Title style={{fontSize: '1rem', fontWeight: 700, color: '#0a2540'}}>{L.booster.orderDetails}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{detailsOrder && (
						<>
							<div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8}}>
								<span style={{color: '#68778d', fontSize: '0.82rem'}}>{L.booster.thService}</span>
								<span style={{color: '#0a2540', fontSize: '0.82rem', fontWeight: 600}}>{detailsOrder.boost_name || detailsOrder.boost_type}</span>
							</div>
							{/* Which account this went to. With several profiles linked, the
							    order rows alone never said — and the handle is the first thing
							    you check when an order looks wrong. */}
							{orderAccount(detailsOrder) && (
								<div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 12}}>
									<span style={{color: '#68778d', fontSize: '0.82rem', flexShrink: 0}}>{L.booster.thAccount}</span>
									<a
										href={orderAccount(detailsOrder).profileUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="text-truncate"
										style={{color: '#635bff', fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none'}}
									>
										@{orderAccount(detailsOrder).handle}
										<i className="fas fa-arrow-up-right-from-square" style={{fontSize: '0.62rem', marginLeft: 5, opacity: 0.75}}></i>
									</a>
								</div>
							)}
							<div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8}}>
								<span style={{color: '#68778d', fontSize: '0.82rem'}}>{L.booster.thAmount}</span>
								<span style={{color: '#0a2540', fontSize: '0.82rem', fontWeight: 600}}>{deliveryQuantity(detailsOrder.quantity)}</span>
							</div>
							<div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8}}>
								<span style={{color: '#68778d', fontSize: '0.82rem'}}>{L.booster.thCharge}</span>
								<span style={{color: '#0a2540', fontSize: '0.82rem', fontWeight: 600}}>{orderPrice(detailsOrder)}</span>
							</div>
							{(orderPrefs(detailsOrder) || []).map(([label, value], i) => (
								<div key={i} style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8, paddingTop: 8, borderTop: i === 0 ? '1px solid #e3e8ee' : 'none'}}>
									<span style={{color: '#68778d', fontSize: '0.82rem'}}>{label}</span>
									<span style={{color: '#0a2540', fontSize: '0.82rem', fontWeight: 600}}>{value}</span>
								</div>
							))}

							{/* Which posts were boosted and by how much. An engagement pack
							    spreads over several posts, so a single row can't say what
							    actually happened. */}
							{detailsOrder.items && detailsOrder.items.length > 0 && (
								<div style={{marginTop: 12, paddingTop: 12, borderTop: '1px solid #e3e8ee'}}>
									<div style={{color: '#68778d', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8}}>
										{L.booster.postsBoosted(new Set(detailsOrder.items.map((it) => it.post_code || it.post_url)).size)}
									</div>
									<div style={{maxHeight: 260, overflowY: 'auto'}}>
										{Object.values(detailsOrder.items.reduce((acc, it) => {
											const k = it.post_code || it.post_url;
											if (!acc[k]) acc[k] = { post_url: it.post_url, thumbnail: it.thumbnail, parts: [] };
											acc[k].parts.push(it);
											return acc;
										}, {})).map((post, i) => (
											<a
												key={i}
												href={post.post_url}
												target="_blank"
												rel="noopener noreferrer"
												style={{display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', textDecoration: 'none', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none'}}
											>
												{post.thumbnail ? (
													<img src={post.thumbnail} alt="" style={{width: 34, height: 34, borderRadius: 6, objectFit: 'cover', flexShrink: 0}} />
												) : (
													<span style={{width: 34, height: 34, borderRadius: 6, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
														<i className="fa-solid fa-image" style={{color: '#94a3b8', fontSize: '0.75rem'}}></i>
													</span>
												)}
												<span style={{flex: 1, minWidth: 0, color: '#0a2540', fontSize: '0.78rem', fontWeight: 600}}>
													{post.parts.map((pt) => `${deliveryQuantity(pt.quantity)} ${String(pt.service).split('-')[1]}`).join(' · ')}
												</span>
												<i className="fa-solid fa-arrow-up-right-from-square" style={{color: '#635bff', fontSize: '0.65rem'}}></i>
											</a>
										))}
									</div>
								</div>
							)}
						</>
					)}
				</Modal.Body>
			</Modal>

			{/* Sticky checkout bar. A size or bundle is picked near the top of a
			    long form, and the CTA that acts on it sits below the order summary —
			    far enough down that the selection could be made and then lost track
			    of. Shown for any selection (hasValue), not just bundles: the quantity
			    tiers are what most visitors pick. When a post is still needed the
			    button says so rather than going dead silently.
			    Hidden behind the confirm popup, which has its own Pay button. */}
			{/* The handle, collected before anything can be bought. It used to sit on
			    the page below the picker, where a followers order — which needs no
			    post, so nothing else gated it — could reach checkout with it left
			    empty and ship an order with no destination. */}
			<Modal show={showProfileModal} onHide={() => setShowProfileModal(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title style={{fontSize: '1rem', fontWeight: 700, color: '#0a2540'}}>{isPostLinkInput ? L.booster.postModalTitle : L.booster.profileModalTitle}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px', display: 'block'}}>{linkLabel(service)}</label>
					<Form.Control
						type="text"
						placeholder={linkPlaceholder(service)}
						value={postLink}
						autoFocus
						onChange={(e) => setPostLink(e.target.value)}
						onKeyDown={(e) => { if (e.key === 'Enter' && String(postLink).trim()) confirmProfile(); }}
						style={{borderRadius: '8px', border: '1.5px solid #e3e8ee', fontSize: '0.88rem', padding: '10px 14px'}}
					/>
					{/* Who the handle resolves to. The buyer confirms a face, not their
					    spelling. A failed lookup warns but never blocks — the scraper
					    being down must not stop a sale. */}
					{canPreviewProfile && (previewLoading || profilePreview || (previewFailed && String(postLink).trim())) && (
						<div style={{display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, padding: '10px 12px', borderRadius: 10, border: '1px solid #e3e8ee', background: '#f8fafc'}}>
							{previewLoading ? (
								<>
									<i className="fas fa-spinner fa-spin" style={{color: '#8898aa'}}></i>
									<span style={{fontSize: '0.8rem', color: '#68778d'}}>{L.booster.profileLookingUp}</span>
								</>
							) : profilePreview ? (
								<>
									<img
										src={profilePreview.photo}
										alt=""
										style={{width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid #e3e8ee'}}
										onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.png'; }}
									/>
									<span style={{minWidth: 0}}>
										<span style={{display: 'block', fontWeight: 700, color: '#0a2540', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>@{profilePreview.username}</span>
										{Number(profilePreview.followers) > 0 && (
											<span style={{display: 'block', color: '#8898aa', fontSize: '0.75rem'}}>
												{Number(profilePreview.followers).toLocaleString()} {String(L.booster.services.followers).toLowerCase()}
											</span>
										)}
									</span>
									<i className="fas fa-circle-check" style={{marginLeft: 'auto', color: '#16a34a', flexShrink: 0}}></i>
								</>
							) : (
								<>
									<i className="fas fa-circle-exclamation" style={{color: '#b45309', flexShrink: 0}}></i>
									<span style={{fontSize: '0.8rem', color: '#b45309'}}>{L.booster.profileNotFound}</span>
								</>
							)}
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={confirmProfile}
						disabled={!String(postLink).trim()}
						style={{
							width: '100%', padding: '12px', borderRadius: 10, border: 'none', fontWeight: 700, fontSize: '0.9rem',
							cursor: String(postLink).trim() ? 'pointer' : 'not-allowed',
							background: String(postLink).trim() ? 'linear-gradient(135deg, #635bff 0%, #8b5cf6 100%)' : '#e2e8f0',
							color: String(postLink).trim() ? '#fff' : '#94a3b8',
						}}
					>
						{L.booster.profileModalContinue}
					</button>
				</Modal.Footer>
			</Modal>

			{stickyReady && !showAddons && (
				<div style={{
					position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1030,
					background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(8px)',
					borderTop: '1px solid #e3e8ee', boxShadow: '0 -4px 16px rgba(50,50,93,0.10)',
					padding: '10px 16px calc(10px + env(safe-area-inset-bottom))',
				}}>
					<div style={{
						maxWidth: 640, margin: '0 auto', display: 'flex',
						alignItems: 'center', justifyContent: 'space-between', gap: 12,
					}}>
						<div style={{minWidth: 0}}>
							<div style={{fontWeight: 700, color: '#0a2540', fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
								{selectedPack
									? L.booster.bundleLabel(selectedPack.likes.toLocaleString(), selectedPack.views.toLocaleString())
									: L.booster.tierLabelService(Number(quantity || 0).toLocaleString(), L.booster.services[service] || service)}
							</div>
							{/* The destination, not a delivery mechanic: "spread across your
							    posts" was only true of likes/views/packs, and read as a false
							    promise beside a followers order. Where the order goes is true
							    for every service and doubles as a last look at the handle. */}
							<div style={{color: '#8898aa', fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
								{postLink
									? L.booster.deliverTo(String(postLink).replace(/^@/, ''))
									: platform.charAt(0).toUpperCase() + platform.slice(1)}
							</div>
						</div>
						{/* Two jobs, never a dead one: lead to the posts while one is
						    still needed, then check out. */}
						<button
							type="button"
							onClick={ctaAction}
							disabled={stickyBusy}
							style={{
								flexShrink: 0, padding: '11px 20px', borderRadius: 10, border: 'none',
								cursor: stickyBusy ? 'default' : 'pointer',
								background: 'linear-gradient(135deg, #635bff 0%, #8b5cf6 100%)',
								color: '#fff',
								fontWeight: 700, fontSize: '0.88rem', whiteSpace: 'nowrap',
								boxShadow: '0 2px 8px rgba(99,91,255,0.35)',
								opacity: stickyBusy ? 0.6 : 1,
							}}
						>
							{stickyBusy
								? <><i className="fas fa-spinner fa-spin" style={{marginRight: 6}}></i>{L.booster.processing}</>
								: ctaPendingText
									? <><i className={`fas ${needsProfile ? 'fa-user' : 'fa-images'}`} style={{marginRight: 6}}></i>{ctaPendingText}</>
									: L.booster.buyNowAmount((selectedPack ? selectedPack.price : (Number(price) || 0)).toFixed(2))}
						</button>
					</div>
				</div>
			)}
		</>
	);
};

export default DeliveryDashboard;
